/**
 * Message catalogs (ADR 0009). English is the source locale; every catalog has the same keys.
 * ICU message syntax for plurals and interpolation. `name.*` keys are interim display names of
 * known ids until the ingredient registry (#26).
 */
import { helpEn, helpFr } from './help';

export const en = {
  'equipment.preheat.unknown': 'preheat unspecified',
  'equipment.fan.unknown': 'unknown',
  'equipment.steam.none': 'none',
  'equipment.cover.unknown': 'unknown',
  'equipment.label.preheat': 'Preheat',
  'equipment.label.details': 'Equipment details',
  'equipment.label.levelLabel': 'Power level',

  'equipment.baking-stone.name': 'Baking stone',
  'equipment.baking-steel.name': 'Baking steel',
  'equipment.aluminium-sheet.name': 'Aluminium baking sheet',
  'equipment.oven-rack.name': 'Oven rack',
  'equipment.dutch-oven-cast-iron.name': 'Cast-iron Dutch oven',
  'equipment.pullman-steel.name': 'Aluminized steel Pullman pan',
  'equipment.pullman-aluminium.name': 'Aluminium Pullman pan',
  'equipment.loaf-pan-steel.name': 'Steel loaf pan',
  'equipment.loaf-pan-silicone.name': 'Silicone loaf pan',
  'equipment.loaf-pan-ceramic.name': 'Ceramic loaf pan',
  'equipment.loaf-pan-glass.name': 'Glass loaf pan',
  'equipment.cast-iron-skillet.name': 'Cast iron skillet',
  'equipment.baguette-tray.name': 'Perforated baguette tray',
  'equipment.pot.name': 'Stainless steel pot',
  'mode.bottom.name': 'Bottom heat',
  'mode.bottom.help': 'Only the bottom element runs, without a fan.',
  'mode.topBottom.name': 'Top and bottom heat',
  'mode.topBottom.help': 'The top and bottom elements run, without a fan.',
  'mode.convection.name': 'Convection (bottom element and fan)',
  'mode.convection.help': 'The bottom element runs with the fan.',
  'mode.trueConvection.name': 'True convection (rear element and fan)',
  'mode.trueConvection.help': 'The rear element runs with the fan, as in European ovens.',
  'mode.convectionRoast.name': 'Convection roast',
  'mode.convectionRoast.help': 'The top and bottom elements run with the fan.',
  'mode.broil.name': 'Broil',
  'mode.broil.help':
    'Only the top element runs, with direct radiant heat; distinct from an outdoor barbecue grill.',
  'mode.convectionBroil.name': 'Convection broil',
  'mode.convectionBroil.help': 'The top element runs with the fan.',
  'mode.steam.name': 'Steam',
  'mode.steam.help': 'The oven’s steam function runs, without dry heat from the elements.',
  'mode.proof.name': 'Proof',
  'mode.proof.help': 'A low-heat program keeps a warm environment for dough rising, not cooking.',
  'mode.custom.name': 'Other',
  'mode.custom.help':
    'An appliance mode without a matching Flourprint mode. The appliance’s own name is kept when known.',
  'equipment.appliance.oven': 'Oven',
  'equipment.appliance.stovetop': 'Stovetop',
  'equipment.appliance.grill': 'Grill (barbecue)',
  'equipment.appliance.steamer': 'Steamer',
  'equipment.appliance.fryer': 'Fryer',
  'equipment.appliance.proofer': 'Proofer',
  'equipment.appliance.other': 'Appliance',
  'equipment.label.setpoint': 'Setpoint',
  'equipment.label.level': 'Power level {value}',
  'equipment.label.levelOf': 'Power level {value} of {of}',
  'equipment.label.fan': 'Fan',
  'equipment.fan.on': 'on',
  'equipment.fan.off': 'off',
  'equipment.label.steam': 'Steam',
  'equipment.steam.function': 'steam function',
  'equipment.steam.manual': 'manual',
  'equipment.label.support': 'Support',
  'equipment.label.vessel': 'Vessel',
  'equipment.cover.open': 'open',
  'equipment.cover.covered': 'covered',
  'equipment.cover.closed': 'closed',
  'equipment.preheat.hot': 'preheated',
  'equipment.preheat.cold': 'cold start',
  'equipment.label.rack': 'Rack',
  'equipment.label.load': 'Load',
  'equipment.label.loadPieces': '{count, plural, one {# piece} other {# pieces}}',
  'equipment.label.loadMass': '{grams} g',
  'equipment.label.loading': 'Loading…',
  'equipment.label.missing': 'Unresolved item',
  'equipment.label.unnamed': 'Unnamed equipment',
  'equipment.label.error': 'Load error',

  'recipe.editFermentation': 'Edit fermentation settings',
  'recipe.editFermentationFor': 'Edit fermentation settings for {step}',

  'recipe.methodTitle': 'Preparation steps',
  'recipe.returnToPlanning': 'Return to planning',
  'planning.editInRecipe': 'Edit in recipe',
  'planning.editStepInRecipe': 'Edit {step} in recipe',
  'formula.hydration.locked': 'Hydration locked at {pct}',
  'formula.hydration.unlocked': 'Hydration unlocked',
  'formula.hydration.lockedExplain': 'Hydration locked. Unlock with the lock button to change.',
  'step.operationType': 'Operation',
  'step.operationBlockedRaw': 'Correct the input before changing the step operation.',
  'step.customName': 'Step name (optional)',
  'step.delete': 'Delete step',
  'step.preparationOwnedRefusal':
    'This step belongs to a preparation. To remove it, remove the component in the Components section.',
  'process.outcomeResult': 'Result: {outcome}',
  'process.outcomeInferredHint': 'Inferred from preparation steps.',
  'process.outcomeAuthoredHint': 'Authored outcome for this recipe.',
  'editor.undo': 'Undo',
  'editor.redo': 'Redo',
  'editor.reset': 'Reset to baseline',
  'editor.resetConfirm':
    'Restore values to the start of this editing session? Unsaved changes and unconfirmed input will be lost.',
  'template.dutchOven.bakeShort': 'Dutch oven bake',
  'template.dutchOven.bake':
    'Bake in preheated 6–8 qt Dutch oven at 450 °F (232 °C): 30 min covered, then 22.5 min uncovered',
  'template.baguette.bakeShort': 'Steam bake',
  'template.baguette.bake': 'Bake at 450 °F (232 °C) on stone or pan with steam',
  'template.ciabatta.bakeShort': 'Steam bake',
  'template.ciabatta.bake': 'Bake at 460 °F (238 °C) with steam',
  'template.bagels.boilShort': 'Water bath simmer',
  'template.bagels.boil': 'Simmer 30 s per side in gentle water bath in batches of 3–4',
  'template.bagels.bakeShort': 'Sheet pan bake',
  'template.bagels.bake': 'Bake on parchment-lined sheet pan at 425 °F (218 °C)',
  'template.focaccia.bakeShort': 'Lower rack pan bake',
  'template.focaccia.bake': 'Bake in pan on lower rack at 475 °F (246 °C)',
  'template.focaccia.bakeFinishShort': 'Residual heat finish',
  'template.focaccia.bakeFinish':
    'Finish out of pan on lower rack with oven turned off (residual heat)',
  'template.pizzaNeoNeapolitan.bakeShort': 'Baking Steel bake',
  'template.pizzaNeoNeapolitan.bake': 'Bake on Baking Steel at 500–550 °F (274 °C)',
  'template.pizzaRoman.bakeShort': 'High-heat oven bake',
  'template.pizzaRoman.bake': 'Bake in professional pizza oven (≤350 °C)',
  'template.pizzaNewYork.bakeShort': 'Stone or steel bake',
  'template.pizzaNewYork.bake': 'Bake on preheated stone or steel at 550 °F (288 °C)',
  'template.pizzaChicago.assemblyShort': 'Skillet layering',
  'template.pizzaChicago.assembly': 'Layer cheeses, vegetables, meats, and sauce in skillet',
  'template.pizzaChicago.bakeShort': 'Cast-iron skillet bake',
  'template.pizzaChicago.bake':
    'Bake in cast-iron skillet at 450 °F (230 °C), tent with foil after 20 min',
  'template.pizzaSicilian.finishStretchShort': 'Pan stretch',
  'template.pizzaSicilian.finishStretch':
    'Stretch to fill pan; repeat 10–15 min rest and stretch manually if needed',
  'template.pizzaSicilian.parBakeShort': 'Sheet pan par-bake',
  'template.pizzaSicilian.parBake':
    'Par-bake untopped crust in 18×13-inch half-sheet pan at 450 °F (232 °C)',
  'template.pizzaSicilian.assemblyShort': 'Crust topping',
  'template.pizzaSicilian.assembly': 'Top par-baked crust with sauce, cheese, and toppings',
  'template.pizzaSicilian.bakeShort': 'Topped pizza bake',
  'template.pizzaSicilian.bake': 'Bake topped pizza at 450 °F (232 °C)',
  'template.pizzaSicilian.coolShort': 'Pre-slicing rest',
  'template.pizzaSicilian.cool': 'Rest before slicing',
  'template.pizzaTeglia.bakeShort': 'Deck oven bake',
  'template.pizzaTeglia.bake': 'Bake in electric deck oven (deck 300–320 °C, top 200 °C, static)',
  'template.brioche.mixShort': 'Butter incorporation mix',
  'template.brioche.mix':
    'Mix 2 min low speed, 8 min medium speed, then incorporate butter gradually (DDT <=26 °C)',
  'template.brioche.shapeShort': 'Tin arrangement shaping',
  'template.brioche.shape': 'Shape into 50 g balls, arrange 7 per loaf tin',
  'template.brioche.proofShort': 'Proofer rise',
  'template.brioche.proof': 'Proof in proofer at 28 °C',
  'template.brioche.bakeShort': 'Fan oven bake',
  'template.brioche.bake': 'Brush with separate egg wash, then bake in fan-assisted oven at 180 °C',
  'template.burgerBuns.shapeShort': 'Bun ball shaping',
  'template.burgerBuns.shape': 'Shape each piece into a ball and flatten to about 3 inches',
  'template.burgerBuns.bakeShort': 'Glazed sheet bake',
  'template.burgerBuns.bake':
    'Brush with half the separate 43 g melted butter, then bake on sheet at 375 °F (191 °C)',
  'template.burgerBuns.coolShort': 'Glazing and cooling',
  'template.burgerBuns.cool': 'Brush with remaining glaze butter and cool on rack',
  'template.pullman.mixShort': 'Full development mix',
  'template.pullman.mix': 'Mix by machine to full gluten development (DDT 24–26 °C)',
  'template.pullman.shapeShort': 'Pullman bâtard shaping',
  'template.pullman.shape':
    'Shape oblong bâtard and place in prepared lidded 13x4x4-inch Pullman pan',
  'template.pullman.proofShort': 'Open pan proof',
  'template.pullman.proof': 'Proof in prepared 13x4x4-inch Pullman pan with the lid off',
  'template.pullman.bakeShort': 'Covered Pullman bake',
  'template.pullman.bake': 'Home bake covered in lidded 13x4x4-inch Pullman pan at 200 °C',
  'template.pullman.bakeVentedShort': 'Vented rack bake',
  'template.pullman.bakeVented': 'Remove lid, invert loaf onto wire rack, bake vented at 200 °C',
  'template.shokupan.tangzhongShort': 'Tangzhong cooking',
  'template.shokupan.tangzhong':
    'Cook 14 g flour, 43 g water and 43 g milk on low heat until thickened',
  'template.shokupan.tangzhongCoolShort': 'Tangzhong cooling',
  'template.shokupan.tangzhongCool':
    'Cool tangzhong to lukewarm before mixing; 60 min is an editable editorial allowance',
  'template.shokupan.shapeShort': 'Loaf log shaping',
  'template.shokupan.shape':
    'Roll and fold each piece into a 4-inch log, place in 9x5-inch loaf pan',
  'template.shokupan.bakeShort': 'Loaf pan bake',
  'template.shokupan.bake':
    'Brush with separate milk wash, then bake in 9x5-inch loaf pan at 350 °F (177 °C)',

  ...helpEn,
  'recipe.trash': 'Move to trash',
  'recipe.trash.title': 'Move to trash?',
  'recipe.trash.confirm':
    '“{name}” leaves the recipe list. Its versions and its bakes are kept, and it can be restored from the trash.',
  'recipe.trashed': 'This recipe is in the trash. Restore it to edit it or start a bake.',
  'recipe.deleted': 'Deleted recipe',
  'trash.title': 'Trash',
  'trash.hint': 'Recipes stay here until deleted by hand. Bakes already made are kept either way.',
  'trash.empty': 'The trash is empty.',
  'trash.item': 'In the trash {when} · version {version}',
  'trash.restore': 'Restore',
  'trash.delete': 'Delete permanently',
  'trash.delete.confirm':
    '“{name}” and all its versions will be deleted for good. This cannot be undone. Bakes already made are kept.',
  'trash.emptyAll': 'Empty trash',
  'trash.emptyAll.confirm':
    '{count, plural, one {# recipe} other {# recipes}} and all their versions will be deleted for good. This cannot be undone. Bakes already made are kept.',
  'advanced.trash': 'Trash',
  'advanced.trashHint': 'Restore a recipe or delete it for good.',
  'error.RecipeTrashed': 'This recipe is in the trash. Restore it first.',
  'error.NotFound': 'This recipe no longer exists.',
  'error.AlreadyTrashed': 'This recipe is already in the trash.',
  'error.NotTrashed': 'This recipe is not in the trash.',
  'recipe.edit': 'Edit',
  'recipe.plan': 'Plan',
  'recipe.consultation': 'Read recipe',
  'recipe.estimateCaption': 'Estimates from the provisional model; they do not change the recipe.',
  'recipe.doughEstimate': 'Dough',
  'recipe.estimatedYeast': '{name}: {pct} estimated for this schedule',
  'formula.finalMix.temporaryScope': 'Temporary checklist while weighing; not saved to history',
  'app.title': 'Flourprint',
  'recipes.deviceChecks': 'Device checks',
  'advanced.title': 'Advanced',
  'checks.title': 'Device checks',
  'checks.intro':
    'Checks for spikes #23 and #24. Run them on the phone, then copy the report into the issue.',
  'checks.environment': 'Environment',
  'checks.build': 'Build',
  'checks.platform': 'Platform',
  'checks.webview': 'WebView',
  'checks.storage': 'Storage',
  'checks.sqlite': 'SQLite',
  'checks.storeTitle': 'Storage contract',
  'checks.run': 'Run the storage checks',
  'checks.running': 'Running…',
  'checks.summary': '{passed} of {total} checks passed',
  'checks.markerTitle': 'Survives a forced stop',
  'checks.markerHelp':
    'Write a marker, force-stop the app from Android settings, reopen it: the same time must show here. Also after installing a newer APK.',
  'checks.markerWrite': 'Write a marker now',
  'checks.markerLast': 'Last marker: {time}',
  'checks.markerNone': 'No marker yet.',
  'checks.markerUnavailable': 'Only available in the installed app.',
  'checks.manualTitle': 'To check by hand',
  'checks.manual.offline': 'Airplane mode on, PC off: the app opens and shows the recipe.',
  'checks.manual.decimal': 'On the formula, type 2,2 then 2.2 in a field: both are accepted.',
  'checks.manual.keyboard':
    'The keyboard shows digits and a decimal separator; the field stays visible.',
  'checks.manual.draft':
    'Change a value, go back to the list, reopen the recipe: the change is still there.',
  'checks.manual.theme': 'Text is readable in light and dark mode.',
  'checks.copy': 'Copy the report',
  'checks.copied': 'Report copied.',
  'state.recipeMissing': 'This recipe does not exist.',
  'state.bakeMissing': 'This bake does not exist.',
  'state.storageRead': 'The data could not be read. Close and reopen the app.',
  'state.storageWrite': 'Nothing could be saved. Your changes are kept; try again.',
  'state.storageInit': 'Flourprint could not open its data. Close and reopen the app.',
  'state.sessionOnly': 'Browser preview: data lives in this tab only and is lost on reload.',
  'state.browserKept': 'Browser preview: data is kept in this browser, on this device only.',
  'state.browserUnreadable':
    'The data saved in this browser could not be read: it is kept aside and the app starts empty.',
  'advanced.browserErase': 'Erase this browser’s data',
  'advanced.browserEraseHint': 'Recipes, drafts, flours and bakes kept by the browser preview.',
  'advanced.browserEraseConfirm':
    'Everything the browser preview kept is erased and the page reloads empty. Copy your data first to keep it.',
  'state.retry': 'Try again',
  'recipes.title': 'Recipes',
  'recipes.empty': 'No recipe yet. Tap + to start from a template or a blank recipe.',
  'recipes.card.dough': 'Dough',
  'recipes.card.hydration': 'Hydration',
  'recipes.card.duration': 'Duration',
  'recipes.card.lastBaked': 'Baked {when}',
  'recipes.card.neverBaked': 'Never baked',
  'recipes.activeBake': 'Baking now · {recipe}',
  'recipes.activeBakeCard': 'Preparation in progress',
  'recipes.resumeBake': 'Resume preparation →',
  'recipe.menu': 'Recipe menu',
  'recipe.nameLabel': 'Recipe name',
  'editor.leaveTitle': 'Leave editing?',
  'editor.leaveDraft': 'The draft is kept. Save it as a recipe before starting a preparation.',
  'editor.leaveInvalid':
    'Unaccepted text cannot be stored. Leave and keep the last accepted draft, discarding this text?',
  'editor.keepDraft': 'Keep draft and leave',
  'outcome.pieces': 'Raw dough pieces kept (optional)',
  'recipes.card.cookingUnknown': 'Cooking not recorded',
  'recipe.editAction': 'Edit recipe',
  'recipe.trashAction': 'Move to trash',
  'recipe.startAction': 'Start preparation',
  'recipe.resumeAction': 'Resume preparation',
  'recipe.tab.comp': 'Composition',
  'recipe.tab.parts': 'Components',
  'recipe.tab.steps': 'Steps',
  'recipe.tab.res': 'Result',
  'editor.menu': 'Editor menu',
  'editor.check': 'Check recipe',
  'editor.review': 'Review changes',
  'editor.discard': 'Discard changes',
  'editor.save': 'Save recipe',
  'editor.draftUnsaved': 'Unsaved changes',
  'editor.draftClean': 'Recipe v{version}',
  'prep.progressPct': '{pct} % of planned time elapsed',
  'prep.sessionStarted': ' · Started {time}',
  'prep.readyMark': 'Ready to shape',
  'prep.plus15': '+15 min',
  'prep.duration': 'Duration',
  'prep.note': 'Note',
  'formula.column.basisFlour': '% of flour',
  'formula.column.basisBaker': 'Baker’s %',
  'formula.column.basisGrams': 'Weight',
  'component.ratioLabel': 'Flour:liquid ratio',
  'component.ratioRejected':
    'The ratio cannot be applied. Check the component’s flour, water and ingredient allocations.',
  'component.ratioUnknown': 'Ratio unavailable without component flour',
  'component.ratioBasisDesc':
    'The ratio counts weighed water and allocated ingredients, including the full mass of milk. Hydration counts their water content.',
  'bake.headerTitle': 'Preparation',
  'bake.overflowActions': 'Preparation actions',
  'bake.finishRaw': 'Finish preparation (raw dough)',
  'bake.timeNotBiological': 'Planned time does not confirm dough state.',
  'bake.completedSteps': 'Completed steps',
  'recipe.overflowActions': 'Recipe actions',
  'editor.overflowActions': 'Edit actions',
  'bakes.title': 'Preparations in progress',
  'bakes.empty': 'No preparation in progress. Start one from a recipe.',
  'bakes.open': 'Open',
  'bakes.unreadable':
    '{count, plural, one {# running bake cannot be read by this version} other {# running bakes cannot be read by this version}}.',
  'bakes.alert.end': '{time} · {step} ends',
  'bakes.alert.start': '{time} · start {step}',
  'recipes.allBakes': 'Follow all {count} bakes',
  'recipe.version': 'Version {version}',
  'recipe.tab.formula': 'Recipe',
  'recipe.tab.process': 'Planning',
  'recipe.tab.history': 'History',
  'recipe.bake': 'Start',
  'recipe.baking': 'In progress',
  'draft.unsaved': 'Unsaved changes',
  'draft.invalid': 'Correct invalid fields',
  'draft.invalidPreview':
    'Displayed values are the last accepted values. Correct invalid fields before starting.',
  'draft.save': 'Save v{version}',
  'draft.saved': 'Saved as version {version}.',
  'draft.discard': 'Discard changes',
  'guide.title': 'About this style',
  'guide.unverified':
    'Typical ranges gathered from baking references, not yet checked against the full sources.',
  'guide.ranges': 'Selected recipe reference values',
  'guide.basis': 'Source and editorial choices (English)',
  'guide.referenceNote':
    'Values describe the selected published variant, not a consensus or a prediction.',
  'guide.use': 'Start from this template',
  'guide.key.hydration_pct': 'Water',
  'guide.key.hydration_pct_high': 'Water (high-hydration family)',
  'guide.key.salt_pct': 'Salt',
  'guide.key.hydration_max_pct': 'Pure water (source maximum)',
  'guide.key.butter_pct': 'Butter (ingredient mass)',
  'guide.key.oil_pct': 'Dough oil',
  'guide.key.added_fats_pct': 'Oil and butter (ingredient mass)',
  'guide.key.non_diastatic_malt_pct': 'Non-diastatic malt powder',
  'guide.key.cornmeal_pct': 'Cornmeal in total flour',
  'guide.key.cold_retard_min_h': 'Cold rest (source minimum)',
  'guide.key.cold_retard_max_h': 'Cold use window (source maximum)',
  'guide.key.bake_max_c': 'Professional oven (guide maximum)',
  'guide.key.simmer_per_side_s': 'Simmer per side, per bagel',
  'guide.key.sugar_pct': 'Sugar',
  'guide.key.fat_pct': 'Fat',
  'guide.key.fat_pct_high': 'Fat (high-hydration family)',
  'guide.key.egg_pct': 'Eggs',
  'guide.key.milk_pct': 'Milk',
  'guide.key.water_pct': 'Water besides eggs',
  'guide.key.liquid_pct': 'Water and milk',
  'guide.key.yeast_instant_pct': 'Instant yeast',
  'guide.key.yeast_active_dry_pct': 'Active dry yeast',
  'guide.key.preferment_pct': 'Flour in the preferment',
  'guide.key.biga_hydration_pct': 'Biga hydration',
  'guide.key.scald_flour_pct': 'Flour in the tangzhong',
  'guide.key.malt_syrup_pct': 'Malt syrup',
  'guide.key.cornmeal_or_semolina_pct': 'Cornmeal or semolina',
  'guide.key.poolish_h': 'Poolish',
  'guide.key.biga_h': 'Biga',
  'guide.key.bulk_h': 'Bulk',
  'guide.key.cold_retard_h': 'Cold fermentation',
  'guide.key.proof_h': 'Proof',
  'guide.key.boil_s': 'Boil',
  'guide.key.bake_c': 'Oven',
  'guide.key.bake_min': 'Bake',
  'guide.key.ball_g': 'Dough ball',
  'guide.key.dough_g_13in': 'Dough for a 13-inch pan',
  'guide.key.dough_g_half_sheet': 'Dough for a half-sheet pan',
  'guide.key.flour_w': 'Flour strength (W)',
  'template.baguette.traits':
    'A long, thin lean loaf of flour, water, salt and yeast, usually on a poolish, baked hot with steam for a thin crisp crust and an open crumb.',
  'template.boule-dutch-oven.traits':
    'A round lean loaf baked in a preheated covered pot that traps steam, then uncovered to brown; wetter than a baguette, with a slow, low-yeast fermentation.',
  'template.ciabatta.traits':
    'A flat Italian slipper loaf from a very wet dough built on a stiff biga, barely shaped, with a large irregular crumb and a thin crust.',
  'template.bagels.traits':
    'Same-day water bagels with non-diastatic malt powder: shaped rings, briefly simmered on each side, then baked on a sheet. No overnight retard in this variant.',
  'template.focaccia.traits':
    'A high-hydration, folded and dimpled flatbread in a square pan. Dough oil is separate from pan and surface oil; a final rack finish uses the switched-off oven.',
  'template.brioche.traits':
    'An egg- and butter-rich French loaf with fresh yeast. Butter is incorporated after initial mixing; this variant selects a one-hour cold rest before shaping.',
  'template.burger-buns.traits':
    'Soft round rolls enriched with egg, butter and sugar, from a water-based dough without milk. Finishing butter is separate from dough butter.',
  'template.pullman.traits':
    'A square sandwich loaf baked in a lidded 13-inch pan. This water-based pain de mie has butter, sugar and malt syrup, without milk or egg.',
  'template.shokupan.traits':
    'An enriched Japanese milk loaf with a cooked tangzhong. This variant uses both water and milk in the paste, with a combined liquid-to-scald-flour ratio of about 6.1:1.',
  'template.pizza-neo-neapolitan.traits':
    'A home-oven pizza baked on a steel: 24 hours of room bulk followed by 48 hours cold, then balling and a separate final rise. This is a home adaptation, not an AVPN certification.',
  'template.pizza-roman.traits':
    'A thin, crisp Roman round pizza from the selected direct professional formula: small balls, no dough oil, and a hot pizza oven.',
  'template.pizza-teglia.traits':
    'Roman pan pizza: a very wet dough of strong flour, matured cold for a day or more, stretched into an oiled pan: airy crumb, crisp base.',
  'template.pizza-new-york.traits':
    'A large, thin, foldable pizza from strong flour with a little oil and often sugar, cold-fermented for one to three days.',
  'template.pizza-sicilian.traits':
    'A thick, airy rectangular pan pizza baked in a well-oiled sheet pan so the base fries crisp.',
  'template.pizza-chicago.traits':
    'A deep-dish home variant with cornmeal, olive oil and butter in the dough, baked in a cast-iron skillet. Cornmeal is included in the flour denominator; no separate final proof is prescribed.',
  'draft.rename': 'Rename',
  'draft.delete': 'Delete the draft',
  'draft.deleteConfirm': 'Delete this draft? This cannot be undone.',
  'draft.discardConfirm': 'Discard the unsaved changes?',
  'draft.newRecipe': 'Draft, not saved yet',
  'draft.saveRecipe': 'Save as a recipe',
  'draft.blankName': 'Recipe',
  'draft.clean': 'Up to date',
  'draft.saveShort': 'Save',
  'draft.reviewShort': 'Changes',
  'draft.discardShort': 'Discard',
  'draft.deleteShort': 'Delete',
  'draft.reviewNew': 'New recipe: no saved revision to compare yet.',
  'draft.review': 'Review changes',
  'draft.reviewTitle': 'Draft changes',
  'draft.reviewFormula': 'Formula changes',
  'draft.reviewProcess': 'Process changes',
  'draft.reviewNoFormula': 'No formula changes.',
  'draft.reviewProcessModified': 'Process parameters modified.',
  'draft.reviewProcessClean': 'No process changes.',
  'check.title': 'Recipe check',
  'check.none': 'Nothing to report: the recipe can be saved and baked.',
  'check.blocking': 'To fix before saving or baking',
  'check.warnings': 'To check (does not block)',
  'check.button': 'Check',
  'check.buttonCount': '{count, plural, one {# point to check} other {# points to check}}',
  'check.no_steps': 'The process has no step.',
  'check.salt_missing': 'No salt: add it with “Add an ingredient”.',
  'check.salt_outside_1_to_3_5': 'Salt at {pct}: unusual (1 to 3.5 % is expected).',
  'check.water_outside_45_to_90': 'Hydration at {pct}: unusual (45 to 90 %).',
  'check.no_leavening': 'No yeast and no preferment: the dough will not rise.',
  'check.no_mix_step': 'No mix step.',
  'check.no_bake_step': 'No bake step.',
  'check.step_without_duration': '{step} has no duration.',
  'recipes.drafts': 'Drafts',
  'recipes.draftEdits': 'Unsaved changes',
  'new.title': 'New recipe',
  'new.blank': 'Blank recipe',
  'new.blankHint': 'Flour, water, salt and yeast, to start from scratch.',
  'new.templates': 'Start from a template',
  'new.templatesHint': 'Typical proportions of the style: a starting point, not a tested recipe.',
  'new.category.bread': 'Breads',
  'new.category.enriched': 'Enriched doughs',
  'new.category.pan': 'Pan loaves',
  'new.category.pizza': 'Pizzas',
  'new.category.flatbread': 'Flatbreads',
  'template.baguette': 'Baguette',
  'template.boule-dutch-oven': 'Dutch-oven boule',
  'template.brioche': 'Brioche',
  'template.burger-buns': 'Burger buns',
  'template.pullman': 'Pullman loaf',
  'template.shokupan': 'Shokupan',
  'template.ciabatta': 'Ciabatta',
  'template.bagels': 'Bagels',
  'template.focaccia': 'Focaccia',
  'template.pizza-neo-neapolitan': 'Neo-Neapolitan pizza',
  'template.pizza-roman': 'Roman pizza (tonda)',
  'template.pizza-new-york': 'New York pizza',
  'template.pizza-chicago': 'Chicago deep-dish pizza',
  'template.pizza-sicilian': 'Sicilian pizza',
  'template.pizza-teglia': 'Roman pan pizza (teglia)',
  'name.tipo-00-flour': 'Tipo 00 flour',
  'name.cornmeal': 'Cornmeal',
  'name.barley-malt-syrup': 'Barley malt syrup',
  'name.biga': 'biga',
  'operation.boil': 'Boil',
  'formula.quantity': 'Recipe size',
  'formula.anchor.flourTotal': 'Flour',
  'formula.anchor.doughWeight': 'Dough',
  'formula.anchor.pieces': 'Pieces',
  'formula.anchorField.flourTotal': 'Total flour',
  'formula.anchorField.doughWeight': 'Dough weight',
  'formula.anchorField.count': 'Number of pieces',
  'formula.anchorField.gramsEach': 'Weight of a piece',
  'formula.stat.dough': 'Dough',
  'formula.stat.hydration': 'Hydration',
  'formula.stat.pureWater': 'Added water',
  'formula.stat.totalWaterValue': '{pct}',
  'formula.stat.totalWaterEstimate': '≈ {pct}',
  'formula.editRule': 'Changing grams keeps the other grams; changing a % keeps the recipe size.',
  'formula.component.flour': '{grams} flour',
  'formula.component.water': '{grams} water',
  'formula.component.allocation': '{grams} {name}',
  'formula.component.loss': '−{grams} evaporated',
  'formula.water.incomplete': 'Hydration is incomplete: unknown water in {ingredients}.',
  'formula.group.flours': 'Flours',
  'formula.group.ingredients': 'Ingredients',
  'formula.column.flourPct': 'Share',
  'formula.column.bakerPct': 'Baker %',
  'formula.column.grams': 'Weight',
  'formula.preparations': 'Preparations',
  'formula.prep.flourPct': '{pct} of the flour',
  'formula.prep.hydration': '{pct} hydration',
  'formula.prep.allocation': '{name} {pct}',
  'formula.finalMix': 'Ingredients',
  'formula.finalMix.progress': '{done} of {total}',
  'explain.title': 'Where the water comes from',
  'explain.pureWater': '{name} (added water)',
  'explain.ingredient': '{name} ({status, select, known {known} estimated {estimated} other {}})',
  'explain.loss': 'Loss in {name}',
  'explain.unknown': '{name}: unknown',
  'explain.close': 'Close',
  'field.invalidNumber': 'Enter a number.',
  'field.invalidDuration': 'Enter a duration such as 45 min or 1 h 30.',
  'process.mode.startAt': 'Start at',
  'process.mode.readyBy': 'Ready by',
  'process.when.startAt': 'Start',
  'process.when.now': 'Now',
  'process.when.chosen': 'At a time',
  'process.when.time': 'Start time',
  'process.startsNow':
    'The preparation starts when tapped: its timers run from that moment. The times above are for planning.',
  'process.when.readyBy': 'End of {step}',
  'process.summary.startAt': '{step} · end {when}',
  'process.summary.readyBy': '{step} · start {when}',
  'process.unknownDay': 'Time unknown',
  'process.target': 'Ready at the end of',
  'process.alap': 'As late as possible',
  'process.after': 'After {steps}',
  'process.unknown': 'Time unknown until {steps} ends',
  'process.duration': 'Duration of {step}',
  'process.openBake': 'Open the preparation in progress',
  'process.startAnother': 'Start another preparation',
  'process.saveFirst': 'Save the changes before starting a preparation.',
  'operation.preferment': 'Preferment',
  'operation.scald': 'Scald',
  'operation.autolyse': 'Autolyse',
  'operation.fermentolyse': 'Fermentolyse',
  'operation.mix': 'Mix',
  'operation.fold': 'Fold',
  'operation.bulk': 'Bulk fermentation',
  'operation.retard': 'Cold retard',
  'operation.divide': 'Divide',
  'operation.preshape': 'Preshape',
  'operation.rest': 'Rest',
  'operation.shape': 'Shape',
  'operation.proof': 'Final proof',
  'operation.preheat': 'Preheat',
  'operation.bake': 'Bake',
  'operation.cool': 'Cool',
  'operation.degas': 'Degas',
  'process.addStep': '+ Add a step',
  'process.addStep.after': 'After',
  'process.addStep.operation': 'New step',
  'process.addStep.confirm': 'Add',
  'process.addStep.cancel': 'Cancel',
  'process.addStep.hint': 'The new step has no duration yet: give it one in the list.',
  'process.editStep': 'Edit step {step}',
  'technique.title': 'Technique',
  'technique.close': 'Close',
  'technique.method': 'Method',
  'technique.none': 'None',
  'technique.method.hand': 'By hand',
  'technique.method.slapAndFold': 'Slap and fold',
  'technique.method.rubaud': 'Rubaud',
  'technique.method.standMixer': 'Stand mixer',
  'technique.method.spiral': 'Spiral',
  'technique.method.foodProcessor': 'Food processor',
  'technique.method.noKnead': 'No-knead',
  'technique.method.stretchAndFold': 'Stretch and fold',
  'technique.method.coilFold': 'Coil fold',
  'technique.method.lamination': 'Lamination',
  'technique.phases': 'Mixing phases',
  'technique.phases.hint': 'The step duration is the sum of the phases.',
  'technique.phase.speed': 'Phase {index} speed',
  'technique.phase.duration': 'Phase {index} duration',
  'technique.phase.remove': 'Remove phase {index}',
  'technique.phase.add': '+ Add a phase',
  'technique.phase.summary': '{duration} {speed}',
  'technique.speed.low': 'low',
  'technique.speed.medium': 'medium',
  'technique.speed.high': 'high',
  'technique.folds': 'Folds during the step',
  'technique.folds.count': 'Number of sets',
  'technique.folds.interval': 'Interval between sets',
  'technique.folds.style': 'Style',
  'technique.folds.remove': 'Remove the folds',
  'technique.folds.hint':
    'Set 1 is due one interval after the step starts, set 2 two intervals after, and so on.',
  'technique.folds.summary':
    '{count, plural, one {# set of folds} other {# sets of folds}} / {interval}',
  'technique.endCheck': 'End of the step',
  'technique.endCheck.windowpane': 'Windowpane',
  'technique.endCheck.doughTemperature': 'Dough temperature',
  'technique.endCheck.feel': 'Feel',
  'technique.endCheck.time': 'Time',
  'technique.endCheck.other': 'Other',
  'technique.endCheck.summary': 'ends on: {check}',
  'technique.removeStep': 'Remove this step',
  'technique.removeConfirm': 'Remove {step}? The steps after it then follow the steps before it.',
  'technique.preparationStep':
    'This step belongs to a preparation: remove the preparation in the Formula tab.',
  'bake.fold.title': 'Folds: {step}',
  'bake.fold.next': 'Set {set}/{count} · in {time}',
  'bake.fold.overdue': 'Set {set}/{count} · {time} late',
  'bake.fold.allDone': 'All {count} sets done',
  'bake.fold.done': 'Fold done',
  'bake.fold.pick': 'Which set was done?',
  'bake.fold.set': 'Set {set}',
  'bake.log.fold': '{time} · Folds {set}/{count} done ({step})',
  'notify.fold.title': 'Folds: set {set}/{count}',
  'notify.fold.body': '{recipe} · {step}, set {set} due',
  'check.folds_exceed_step': 'The folds of {step} last longer than the step.',
  'history.empty': 'No preparation yet. Start one with the Start preparation button.',
  'history.item': '{date} · version {version}',
  'history.status.notStarted': 'Not started',
  'history.status.active': 'In progress',
  'history.status.finished': 'Finished',
  'history.status.abandoned': 'Abandoned',
  'bake.now': 'Now',
  'bake.upNext': 'Up next',
  'bake.position': 'Step {index} of {total}',
  'bake.left': 'left',
  'bake.over': 'over',
  'bake.planned': 'planned',
  'bake.stepPlannedDuration': 'Planned: {duration}',
  'bake.startedAt': 'Started at {time}',
  'bake.endsAt': 'Planned end {time}',
  'bake.startsAt': 'Planned start {time}',
  'bake.noDuration': 'No planned duration',
  'bake.done': '{step} done',
  'bake.start': 'Start {step}',
  'bake.plus15': '+15 min',
  'bake.adjust': 'Duration',
  'bake.note': 'Note',
  'bake.doughReady': 'Dough ready',
  'bake.readyToShape': 'Ready to shape',
  'bake.readyToBake': 'Ready to bake',
  'bake.log.ready': '{time} · {label} ({details}) {text}',
  'state.writeBusy': 'Another change is being saved. Try again in a moment.',
  'bake.doughReadyTitle': 'Dough ready: what did you go by?',
  'bake.doughReady.poke': 'Poke test',
  'bake.doughReady.feel': 'Feel',
  'bake.doughReady.time': 'Time',
  'bake.doughReady.other': 'Other',
  'bake.next': 'Next',
  'bake.dueNow': 'Also due now',
  'bake.startShort': 'Start',
  'bake.doneShort': 'Done',
  'bake.alsoRunning': 'Also running',
  'bake.allDone': 'All steps are done.',
  'bake.finish': 'Finish the bake',
  'bake.abandon': 'Abandon preparation',
  'bake.abandonConfirm': 'Abandon this preparation? Its log is kept.',
  'bake.cancel': 'Cancel',
  'bake.save': 'Save',
  'bake.adjustTitle': 'Planned duration of {step}',
  'bake.noteTitle': 'Note',
  'bake.notePlaceholder': 'Dough temperature, feel, rise…',
  'bake.log': 'Log',
  'bake.log.started': '{time} · {step} started',
  'bake.log.completed': '{time} · {step} done',
  'note.step': 'Step',
  'note.wholeBake': 'Whole bake',
  'note.category': 'About',
  'note.category.fermentation': 'Fermentation',
  'note.category.handling': 'Handling',
  'note.category.volume': 'Volume',
  'note.category.crust': 'Crust',
  'note.category.crumb': 'Crumb',
  'note.category.flavor': 'Flavor',
  'note.category.other': 'Other',
  'note.rating': 'Overall',
  'bake.addNote': 'Add a note',
  'bake.log.noteOn': '{time} · {step}: {text}',
  'bake.log.note': '{time} · {text}',
  'bake.finished': 'Preparation finished',
  'bake.abandoned': 'Preparation abandoned',
  'error.InvalidProcess': 'This process is not valid.',
  'error.DependencyCycle': 'These steps depend on each other in a loop.',
  'error.unknown': 'Something went wrong ({code}).',
  'error.FlourAllocationExceeded':
    'The preparations need {required} of {flour}, but the formula only has {available}.',
  'error.WaterAllocationExceeded':
    'The preparations need {required} of water, but the formula only has {available}.',
  'error.IncompatibleConstraint': 'This change is not possible with the current quantity.',
  'error.InvalidFormula': 'This value is not valid.',
  'error.HeadConflict': 'The recipe changed meanwhile. Reload it before saving.',
  'error.DependenciesNotCompleted': 'Finish the previous steps first.',
  'error.StepAlreadyCompleted': 'This step is already done.',
  'error.StepAlreadyStarted': 'This step has already started.',
  'error.BakeNotActive': 'This bake is not in progress.',
  'operation.steam': 'Steam',
  'operation.fry': 'Fry',
  'operation.panCook': 'Pan-cook',
  'operation.grill': 'Grill (barbecue)',
  'outcome.cooked': 'Cooked',
  'outcome.dough': 'Raw dough',
  'outcome.cookedAndDough': 'Cooked and raw dough',
  'outcome.withDough': '{result}, {dough}',
  'outcome.dough.frozen': 'frozen',
  'outcome.dough.refrigerated': 'refrigerated',
  'outcome.dough.given': 'given',
  'outcome.dough.sold': 'sold',
  'outcome.dough.other': 'other',
  'outcome.unknown': 'Not recorded',
  'outcome.auto': 'Automatic: {outcome}',
  'outcome.pick': 'Result',
  'bake.finishAs': 'Finish · {outcome}',
  'bake.finishOther': 'Another result',
  'bake.finishNow': 'Finish now…',
  'outcome.pickDough': 'What became of the raw dough?',
  'outcome.dough.unknown': 'Not said',
  'bake.outcome': 'Result: {outcome}',
  'bake.outcome.change': 'Change the result',
  'bake.reopen': 'Reopen',
  'bake.reopenConfirm':
    'The bake becomes active again to record more steps. Its earlier end stays in the history.',
  'process.intendedOutcome': 'Ends with',
  'process.intendedOutcomeHint':
    'Recorded when the bake is finished, unless another result is chosen at that moment.',
  'stats.results': 'Results of finished bakes',
  'stats.cookingStages': 'Cooking stages done',
  'error.NotFinished': 'Only a finished bake has a result to change.',
  'error.BakeActive': 'This bake is still in progress.',
  'error.VersionConflict': 'The bake changed meanwhile. It has been reloaded.',
  'name.bread-flour': 'Bread flour',
  'name.whole-wheat': 'Whole wheat flour',
  'name.water': 'Water',
  'name.salt': 'Salt',
  'name.instant-yeast': 'Instant yeast',
  'name.fresh-yeast': 'Fresh yeast',
  'name.yeast': 'Yeast (form unspecified)',
  'name.milk-powder': 'Milk powder',
  'name.non-diastatic-malt-powder': 'Non-diastatic malt powder',
  'name.poolish': 'poolish',
  'name.tangzhong': 'tangzhong',
  'name.flour': 'Flour',
  'name.spelt-flour': 'Spelt flour',
  'name.brown-flour': 'Brown flour',
  'name.active-dry-yeast': 'Active dry yeast',
  'name.milk': 'Milk',
  'name.butter': 'Butter',
  'name.egg': 'Egg',
  'name.honey': 'Honey',
  'name.sugar': 'Sugar',
  'name.olive-oil': 'Olive oil',
  'name.sour-cream': 'Sour cream',
  'name.cider-vinegar': 'Cider vinegar',
  'name.diastatic-malt': 'Diastatic malt',
  'style.tangzhong': 'tangzhong',
  'style.yudane': 'yudane',
  'style.scald': 'scald',
  'style.poolish': 'poolish',
  'style.biga': 'biga',
  'style.preferment': 'preferment',
  'style.soaker': 'soaker',
  'style.custom': 'preparation',
  'formula.stat.fat': 'Fat',
  'formula.stat.sugar': 'Sugar',
  'formula.enrichment.incomplete': 'Unknown for: {ingredients}.',
  'advanced.feedback': 'Data for review',
  'advanced.copyAll': 'Copy all my data',
  'advanced.copyAllHint':
    'Recipes, bakes, notes and timings in one block, to paste to a person or an AI agent.',
  'advanced.saveAll': 'Save all my data as a file',
  'advanced.exporting': 'Preparing…',
  'advanced.copied':
    'Copied: {size} KB, {bakes, plural, =0 {no bake} one {# bake} other {# bakes}}.',
  'advanced.saved': 'Saved: {size} KB, {bakes, plural, =0 {no bake} one {# bake} other {# bakes}}.',
  'advanced.copyFailed': 'The copy failed.',
  'advanced.exportFailed': 'The data could not be read.',
  'notify.channel': 'Bake reminders',
  'notify.end.title': '{step}: time is up',
  'notify.end.body': '{recipe} · planned end of {step}',
  'notify.start.title': 'Time to start {step}',
  'notify.start.body': '{recipe} · planned start of {step}',
  'advanced.notifications': 'Reminders',
  'advanced.notify.granted': 'Reminders are on.',
  'advanced.notify.denied': 'Reminders are blocked in the system settings.',
  'advanced.notify.prompt': 'Reminders are not allowed yet.',
  'advanced.notify.none': 'This device cannot show reminders.',
  'advanced.notify.web': 'Reminders show while the app is open.',
  'advanced.notify.allow': 'Allow reminders',
  'advanced.notify.exact': 'Allow on-time reminders',
  'advanced.notify.exactHint': 'Without it, Android may deliver reminders several minutes late.',
  'advanced.recipes': 'Recipes',
  'advanced.devUpdate': 'Load the latest dev build',
  'advanced.devUpdate.hint': 'Running {label}. Dev builds only.',
  'advanced.devUpdate.checking': 'Checking the deployed build…',
  'advanced.devUpdate.current': '{label} is already the latest build.',
  'advanced.devUpdate.loading': 'Loading {label}…',
  'advanced.devUpdate.unreachable': 'The site cannot be reached. Check the connection.',
  'advanced.devUpdate.saveFailed':
    'A newer build is available, but the data could not be saved first: nothing was reloaded.',
  'advanced.sample': 'Load the sample recipes',
  'advanced.sampleHint':
    'Development builds only: adds Poolish + tangzhong for trying the app. A recipe already here is kept.',
  'advanced.import': 'Import recipes…',
  'advanced.importHint':
    'Adds the recipes of a Flourprint recipes file. Recipes already here are kept as they are.',
  'advanced.importing': 'Importing…',
  'advanced.imported':
    '{imported, plural, =0 {No new recipe} one {# recipe imported} other {# recipes imported}}{skipped, plural, =0 {} one {, # already here} other {, # already here}}.',
  'advanced.importUnreadable': 'This file cannot be read as JSON.',
  'error.InvalidRecipeFile': 'This is not a valid Flourprint recipes file.',
  'error.UnsupportedDocumentVersion': 'This file comes from a newer version of Flourprint.',
  'error.NotInFamily': 'This recipe is not a variant of this family.',
  'error.FamilyNotFound': 'This family no longer exists.',
  'error.RecipeNotFound': 'This recipe no longer exists.',
  'family.variants': '{count, plural, one {# variant} other {# variants}}',
  'family.title': 'Variants of {family}',
  'family.default': 'Shown by default',
  'family.pin': 'Show this variant by default',
  'family.unpin': 'Show the most recent by default',
  'family.mostRecent': 'Most recent is shown by default',
  'formula.lock.follows': 'Follows the locked hydration',
  'formula.hydration.lock': 'Lock the hydration at {pct}',
  'formula.hydration.unlock': 'Unlock the hydration',
  'formula.hydration.breakdown': 'Where the water comes from',
  'formula.removeNamed': 'Remove: {name}',
  'formula.remove': 'Remove',
  'formula.add.ingredient': '+ Add an ingredient',
  'formula.add.flour': '+ Add a flour',
  'formula.add.flourHint': 'It is added at 0 %: set the shares so that the flours add up to 100 %.',
  'formula.unresolved': 'The quantities cannot be computed yet: check the recipe to see why.',
  'formula.add.other': 'Other…',
  'formula.add.name': 'Name',
  'formula.add.water': 'Water content in % (empty if unknown)',
  'formula.add.hint': 'It is added at 0 %: set its quantity in the list.',
  'formula.add.confirm': 'Add',
  'formula.add.cancel': 'Cancel',
  'formula.add.poolish': '+ Poolish',
  'formula.add.tangzhong': '+ Tangzhong',
  'formula.prep.flourField': 'Share of the flour',
  'formula.prep.hydrationField': 'Hydration',
  'formula.changes.title': 'Changes not saved yet',
  'formula.changes.added': '{name} added',
  'formula.changes.removed': '{name} removed',
  'formula.changes.changed': '{name}, {field}: {before} → {after}',
  'formula.changes.field.bakerPct': 'baker’s %',
  'formula.changes.field.sharePct': 'share',
  'formula.changes.field.flourPct': 'share of the flour',
  'formula.changes.field.hydrationPct': 'hydration',
  'formula.changes.locked': 'Hydration locked at {pct}',
  'formula.changes.unlocked': 'Hydration unlocked',
  'formula.changes.anchor': 'Recipe size changed',
  'formula.time.title': 'Times',
  'fermentation.title': 'Fermentation',
  'fermentation.provisional': 'provisional',
  'fermentation.mode.basic': 'Basic',
  'fermentation.mode.advanced': 'Advanced',
  'fermentation.room': 'Room temperature',
  'fermentation.target': 'Target dough temperature',
  'fermentation.friction': 'Mixer friction',
  'fermentation.water': 'Water at {temperature}',
  'fermentation.note.friction_not_counted': 'mixer friction not counted',
  'fermentation.note.preferment_at_room_temperature': 'preferment taken at room temperature',
  'fermentation.note.flour_at_room_temperature': 'flour at room temperature',
  'fermentation.note.water_below_0C': 'below {low}: not reachable with water alone',
  'fermentation.note.water_above_60C': 'above {high}: too hot for the yeast',
  'fermentation.stepTemperature': '{step}, temperature',
  'fermentation.unbounded': 'not reached within {days} days',
  'fermentation.reachedBefore': 'The target is reached before {step}.',
  'fermentation.applyDuration': 'Use this time',
  'fermentation.progress': 'The plan reaches {pct} of ripeness',
  'fermentation.readiness': '“Ready to bake” target',
  'fermentation.readiness.model': 'Model end point',
  'fermentation.readiness.experimental': 'Experimental (×2)',
  'fermentation.readinessHint.model':
    'The model’s own end point (“about doubled once”), uncalibrated. The default.',
  'fermentation.readinessHint.experimental':
    'An assumed prior, not measured: about twice the model’s time, from planned schedules. Its range is a sensitivity range.',
  'fermentation.bandOutsideDomain': 'Part of the yeast range lies outside the model’s dose domain.',
  'fermentation.reason.readiness_profile_unknown':
    'This recipe uses a “ready to bake” target this version does not know.',
  'fermentation.reason.invalid_readiness_profile': 'The “ready to bake” target is not valid.',
  'fermentation.reason.non_finite_result': 'The model gives no finite result here.',
  'fermentation.extrapolated': 'Outside the model’s data: {reasons}',
  'fermentation.extra.preferment': 'preferment',
  'fermentation.extra.sugar_above_5pct': 'more than 5 % sugar',
  'fermentation.extra.fat_above_3pct': 'more than 3 % fat',
  'fermentation.extra.salt_outside_1_5_to_3pct': 'salt outside 1.5 to 3 %',
  'fermentation.extra.water_outside_55_to_75pct': 'hydration outside 55 to 75 %',
  'fermentation.extra.sugar_unknown': 'sugar unknown',
  'fermentation.extra.fat_unknown': 'fat unknown',
  'fermentation.extra.water_unknown': 'hydration unknown',
  'fermentation.reason.no_commercial_yeast': 'No ingredient is marked as commercial yeast.',
  'fermentation.reason.temperature_unknown': 'Enter the room temperature to get the times.',
  'fermentation.reason.dose_outside_domain':
    'The yeast dose is outside the model’s range (0.01 to 3 % fresh-yeast equivalent).',
  'fermentation.reason.temperature_outside_domain':
    'A temperature is outside the model’s range ({low} to {high}).',
  'fermentation.reason.end_point_not_reached':
    'The dough would not reach its end point within three weeks.',
  'fermentation.reason.no_mix_step': 'The process has no mix step.',
  'fermentation.reason.no_fermentation_step': 'No fermentation step follows the mix.',
  'fermentation.reason.formula_invalid': 'The formula is not valid.',
  'fermentation.reason.preferment_yeast_unknown':
    'The yeast of this preferment is unknown: give it some yeast.',
  'fermentation.reason.no_preferment_step': 'This preferment has no step.',
  'fermentation.reason.duration_unknown': 'Give every step a time to get the yeast dose.',
  'fermentation.reason.other': 'No estimate ({reason}).',
  'fermentation.markYeast': 'Which ingredient is the yeast?',
  'fermentation.yeastForm': 'Yeast form',
  'fermentation.form.active-dry': 'active dry',
  'fermentation.form.instant': 'instant',
  'fermentation.form.fresh': 'fresh',
  'fermentation.markYeastConfirm': 'Mark as yeast',
  'error.IncompatibleConstraint.waterTarget.locked':
    'This water follows the locked hydration: change the hydration or unlock it.',
  'error.IncompatibleConstraint.waterTarget.unknown':
    'The hydration cannot be locked: the water content of {ingredients} is unknown.',
  'error.IncompatibleConstraint.waterTarget.tooLow':
    'The other ingredients already bring more water than this hydration.',
  'error.IncompatibleConstraint.allocation':
    'A preparation uses this ingredient: remove it there first.',
  'recipes.card.durationAtLeast': '≥ {duration}',
  'recipe.retryStart': 'The previous start was not confirmed: it is sent again exactly as it was.',
  'check.intro': 'Points to fix block saving and baking; the others are advice.',
  'formula.prep.empty':
    'No preparation. A poolish or a tangzhong makes part of the flour ahead of the mix.',
  'process.plan': 'Plan',
  'fermentation.waterTerm': 'Water temperature',
  'fermentation.extrapolationTerm': 'Outside the model’s data',
  'fermentation.timeTerm': 'Model time and range',
  'fermentation.schedule': 'Schedule',
  'fermentation.schedule.pick': 'Apply a schedule…',
  'fermentation.schedule.confirmTitle': 'Apply “{name}”?',
  'fermentation.schedule.confirmMessage':
    'It replaces the fermentation steps ({steps}). The folds of the bulk are kept.',
  'fermentation.schedule.apply': 'Apply',
  'fermentation.schedule.refused':
    'This schedule cannot replace these steps: one adds ingredients or has a technique other than folds.',
  'fermentation.schedule.term': 'Fermentation schedules',
  'schedule.bulk-proof': 'Bulk and proof',
  'schedule.cold-bulk': 'Slow cold bulk',
  'schedule.cold-proof': 'Cold-retarded proof',
  'schedule.same-day-short': 'Same day, short',
  'fermentation.doughSteps': 'Dough steps',
  'fermentation.noDoughSteps': 'No fermentation step between the mix and the bake.',
  'fermentation.location.room': 'room',
  'fermentation.location.fridge': 'fridge',
  'fermentation.stepDuration': '{step}, duration',
  'fermentation.auto': 'Auto',
  'fermentation.autoLabel': '{step}: duration from the model',
  'fermentation.autoTerm': 'Duration from the model (Auto)',
  'fermentation.autoValue': '{step}: about {duration} by the model, details',
  'fermentation.autoDetail':
    'By the model: {duration}, between {low} and {high} depending on its parameters.',
  'fermentation.autoKept': 'The model cannot estimate it now: the last time is kept.',
  'fermentation.autoOff': 'Turn Auto off to type a time.',
  'fermentation.removeStep': 'Remove {step}',
  'fermentation.removeConfirm': 'Remove the step “{step}”?',
  'fermentation.remove': 'Remove',
  'fermentation.cancel': 'Cancel',
  'fermentation.add': '+ Add a fermentation step',
  'fermentation.add.operation': 'Kind of step',
  'fermentation.add.after': 'Placed after',
  'fermentation.add.confirm': 'Add the step',
  'fermentation.result.covers': 'The plan reaches {pct} of the target',
  'fermentation.result.time': '{step}: about {duration} by the model',
  'fermentation.details': 'Details',
  'fermentation.detail.time': 'By the model, {step}: {duration}, between {low} and {high}.',
  'fermentation.detail.timeUnbounded': 'By the model, {step}: {duration}; no reliable range.',
  'fermentation.detail.progress':
    'Depending on the model’s parameters, the plan reaches between {low} and {high} of the target.',
  'fermentation.detail.yeast':
    '{name} for these times: between {low} and {high} depending on the model’s parameters.',
  'fermentation.yeastFor': 'Yeast for these times: {pct}',
  'fermentation.yeastForNamed': '{name} for these times: {pct}',
  'fermentation.yeastCurrent': '(current dose)',
  'fermentation.useYeast': 'Use',
  'fermentation.useYeastLabel': 'Use {pct} of {name}',
  'fermentation.preferments': 'Preferments',
  'fermentation.yeastName': 'Yeast',
  'fermentation.prefermentDetail':
    'By the model, ripe after {duration}, between {low} and {high} depending on its parameters.',
  'fermentation.detail.progressRipe':
    'Depending on the model’s parameters, the plan reaches between {low} and {high} of ripeness.',
  'fermentation.prefermentRipe': 'Ripe after about {duration} by the model',
  'name.all-purpose-flour': 'All-purpose flour',
  'name.bise-flour': 'High-extraction (bise) flour',
  'name.whole-grain-wheat': 'Whole grain wheat flour',
  'flours.title': 'Flours',
  'flours.new': 'New flour',
  'flours.newName': 'My flour',
  'flours.personal': 'Personal flours',
  'flours.builtIn': 'Built-in flours',
  'flours.empty': 'No personal flour yet. Customize a built-in flour or create one.',
  'flours.showArchived': 'Show archived flours',
  'flours.archived': 'Archived',
  'flours.version': 'Version {version}',
  'flours.basedOn': 'Based on {name}',
  'flours.known':
    '{count, plural, =0 {No property known} one {# property known} other {# properties known}}',
  'flours.builtInNote':
    'A built-in flour describes a category, not a bag: its values are typical and unverified (≈). It is never edited: customize it to record what the bag or the mill says.',
  'flours.typical': 'Typical values',
  'flours.estimateValue': '≈ {value} ± {uncertainty}',
  'flours.estimatedHint': 'Typical value of the category: replace it with the bag’s.',
  'flours.summary.estimate': '≈ {value}',
  'flours.summary.proteinPct': 'Protein {value}',
  'flours.summary.ashPct': 'Ash {value}',
  'flours.summary.fallingNumberS': 'Falling number {value}',
  'flours.summary.alveographW': 'W {value}',
  'flours.summary.alveographPL': 'P/L {value}',
  'flours.summary.malted': 'malted',
  'flours.summary.organic': 'organic',
  'flour.all-purpose-flour.description':
    'White wheat flour for most uses: breads, cakes, cookies. In Canada it is milled from hard wheat, strong enough for bread.',
  'flour.bread-flour.description':
    'White wheat flour with more protein than all-purpose, for breads that need strong gluten.',
  'flour.bise-flour.description':
    'High-extraction flour, between white and whole grain: part of the bran is kept. Used for country breads.',
  'flour.whole-wheat.description':
    'In Canada, flour that keeps the bran but may have part of the germ removed (to verify). Used for whole wheat breads, often blended with white flour.',
  'flour.whole-grain-wheat.description':
    'Flour milled from the whole kernel: bran, germ and endosperm. Used for whole grain breads.',
  'flour.flour.description':
    'Generic flour, kept for the recipes that name it without saying which.',
  'flour.spelt-flour.description':
    'Flour from spelt, an old wheat species. Its gluten is more fragile than bread wheat’s; it can be white or whole grain.',
  'flour.brown-flour.description':
    'Wheat flour that keeps part of the bran, between white and whole wheat.',
  'flour.tipo-00-flour.description':
    'Italian grade of the most finely milled white flour. Its protein depends on the use: pizza grades are the strong ones.',
  'flour.cornmeal.description':
    'Coarsely ground dried corn, without gluten-forming proteins. Used for cornbread, polenta or to dust a peel.',
  'flours.customize': 'Customize',
  'flours.save': 'Save',
  'flours.saving': 'Saving…',
  'flours.archive': 'Archive',
  'flours.unarchive': 'Unarchive',
  'flours.close': 'Close',
  'flours.identity': 'Flour',
  'flours.properties': 'Properties',
  'flours.field.name': 'Name',
  'flours.field.cereal': 'Cereal',
  'flours.field.refinement': 'Extraction',
  'flours.field.classificationSystem': 'Classification system (CA, FR, IT…)',
  'flours.field.classificationCode': 'Classification code (T65, 00…)',
  'flours.field.brand': 'Brand',
  'flours.field.product': 'Product',
  'flours.field.notes': 'Notes',
  'flours.field.organic': 'Organic',
  'flours.field.value': '{property}: value',
  'flours.field.basis': '{property}: basis',
  'flours.field.source': '{property}: source',
  'flours.field.detail': '{property}: source detail (optional)',
  'flours.cereal.wheat': 'Wheat',
  'flours.cereal.spelt': 'Spelt',
  'flours.cereal.rye': 'Rye',
  'flours.cereal.corn': 'Corn',
  'flours.cereal.other': 'Other',
  'flours.cereal.unknown': 'Unknown',
  'flours.refinement.white': 'White',
  'flours.refinement.partial': 'Partly whole (part of the bran)',
  'flours.refinement.wholeGrain': 'Whole grain',
  'flours.refinement.unknown': 'Unknown',
  'flours.prop.proteinPct': 'Protein (%)',
  'flours.prop.ashPct': 'Ash (%)',
  'flours.prop.fallingNumberS': 'Falling number (s)',
  'flours.prop.alveographW': 'Alveograph W (10⁻⁴ J)',
  'flours.prop.alveographPL': 'Alveograph P/L',
  'flours.prop.malted': 'Malt added by the mill',
  'flours.unknown': 'Unknown',
  'flours.yes': 'Yes',
  'flours.no': 'No',
  'flours.basis.asIs': 'As sold',
  'flours.basis.moisture14': 'At 14 % moisture',
  'flours.basis.dryMatter': 'On dry matter',
  'flours.source.package': 'Package label',
  'flours.source.producer': 'Mill or producer sheet',
  'flours.source.lab': 'Lab analysis',
  'flours.source.reference': 'Published reference',
  'flours.source.personal': 'Own judgement',
  'flours.invalidNumber': 'Not a valid number.',
  'flours.invalid': 'Some values are not valid: check the marked fields.',
  'flours.conflict': 'This flour changed meanwhile. It has been reloaded: apply the change again.',
  'flours.nameRequired': 'A personal flour needs a name.',
  'error.InvalidFlourEntry': 'This flour entry is not valid.',
  'advanced.flours': 'Flours',
  'advanced.floursHint': 'Built-in and personal flours, with what is known about them',
  // Simulation mode (ADR 0014)
  'sim.banner': 'Simulation mode · simulated time {time}',
  'sim.advance': 'Advance time',
  'sim.advance.next': 'To the next planned moment',
  'sim.advance.nextAt': '{time} · {step}',
  'sim.advance.none': 'No planned moment ahead.',
  'sim.advance.quarter': '+15 min',
  'sim.advance.hour': '+1 h',
  'advanced.simulation': 'Simulation',
  'advanced.simulation.toggle': 'Simulation mode',
  'advanced.simulation.hint':
    'Bakes started while it is on are simulated: they have their own history and statistics, and time can be advanced. Recipes, drafts and flours are shared.',
  'advanced.history': 'Bake history',
  'advanced.stats': 'Statistics',
  'advanced.statsHint': 'Bakes of the current mode, real or simulated',
  'advanced.eraseReal': 'Erase the real history',
  'advanced.eraseSimulation': 'Erase the simulation history',
  'advanced.eraseHint': 'Every bake of this kind, running ones included. Recipes are kept.',
  'advanced.eraseConfirm':
    '{count, plural, =0 {No bake to erase.} one {# bake will be erased, running ones included.} other {# bakes will be erased, running ones included.}} This cannot be undone: copy all data first to keep a record.',
  'advanced.erase': 'Erase',
  'advanced.erased':
    '{count, plural, =0 {No bake erased.} one {# bake erased.} other {# bakes erased.}}',
  'advanced.eraseFailed': 'The history could not be erased.',
  // Appearance / theme
  'advanced.theme': 'Appearance',
  'advanced.theme.system': 'System',
  'advanced.theme.light': 'Light',
  'advanced.theme.dark': 'Dark',
  'advanced.theme.amoled': 'AMOLED',
  'advanced.theme.hint': 'System follows the device setting. Light, Dark and AMOLED remain fixed.',
  // Temperature display units (values-and-units.md)
  'advanced.units': 'Temperature units',
  'advanced.units.oven': 'Oven',
  'advanced.units.cold': 'Cold',
  'advanced.units.ambient': 'Ambient',
  'advanced.units.hint':
    'Oven: bake, preheat, boil. Cold: fridge, freezer, retard. Ambient: everything else, dough and water included. Values are always stored in °C.',
  'recipe.startConfirm': 'Start the preparation now?',
  'recipe.startConfirmSimulation':
    'Simulation mode: the preparation goes to the simulation history.',
  'recipe.startNow': 'Start',
  // Statistics (ADR 0014)
  'stats.title': 'Statistics',
  'stats.mode.physical': 'Real bakes',
  'stats.mode.simulation': 'Simulated bakes',
  'stats.empty': 'No bake yet in this mode.',
  'stats.counts': 'Bakes',
  'stats.total': 'Total',
  'stats.finished': 'Finished',
  'stats.abandoned': 'Abandoned',
  'stats.active': 'In progress',
  'stats.recipes': 'Most baked',
  'stats.bakeCount': '{count, plural, one {# bake} other {# bakes}}',
  'stats.durations': 'Planned and recorded times',
  'stats.durationsHint':
    'Median by operation: the plan at the start of the bake against the time recorded.',
  'stats.durationLine': '{planned} planned · {recorded} recorded · n = {n}',
  'stats.longer': '+{duration}',
  'stats.shorter': '−{duration}',
  'stats.onPlan': 'on plan',
  'stats.readiness': '“Dough ready” marks',
  'stats.readinessCount': '{count, plural, one {# mark} other {# marks}}',
  'stats.readinessCompared':
    'Median time in the step at the mark: {recorded}; model: {model} ({difference}), over {n, plural, one {# mark} other {# marks}}.',
  'stats.readinessNoModel': 'No mark on a step with a model estimate yet.',
  'stats.ratings': 'Average rating',
  'stats.ratingLine': '{average}/5 · {n, plural, one {# rating} other {# ratings}}',
  'stats.recent': 'Last bakes',
  'stats.recentLine': '{date} · {status}',
  'stats.nothing': 'Nothing recorded yet.',
} as const;

export type MessageKey = keyof typeof en;
export type Catalog = Record<MessageKey, string>;

export const fr: Catalog = {
  'equipment.preheat.unknown': 'préchauffage non précisé',
  'equipment.fan.unknown': 'inconnue',
  'equipment.steam.none': 'aucune',
  'equipment.cover.unknown': 'inconnu',
  'equipment.label.preheat': 'Préchauffage',
  'equipment.label.details': 'Détails de l’équipement',
  'equipment.label.levelLabel': 'Niveau de puissance',

  'equipment.baking-stone.name': 'Pierre de cuisson',
  'equipment.baking-steel.name': 'Plaque d’acier',
  'equipment.aluminium-sheet.name': 'Plaque en aluminium',
  'equipment.oven-rack.name': 'Grille de four',
  'equipment.dutch-oven-cast-iron.name': 'Cocotte en fonte',
  'equipment.pullman-steel.name': 'Moule Pullman en acier aluminé',
  'equipment.pullman-aluminium.name': 'Moule Pullman en aluminium',
  'equipment.loaf-pan-steel.name': 'Moule à cake en acier',
  'equipment.loaf-pan-silicone.name': 'Moule à cake en silicone',
  'equipment.loaf-pan-ceramic.name': 'Moule à cake en céramique',
  'equipment.loaf-pan-glass.name': 'Moule à cake en verre',
  'equipment.cast-iron-skillet.name': 'Poêle en fonte',
  'equipment.baguette-tray.name': 'Moule perforé à baguettes',
  'equipment.pot.name': 'Faitout en inox',
  'mode.bottom.name': 'Cuisson traditionnelle',
  'mode.bottom.help': 'Seul l’élément du bas chauffe, sans ventilateur.',
  'mode.topBottom.name': 'Éléments du haut et du bas',
  'mode.topBottom.help': 'Les éléments du haut et du bas chauffent, sans ventilateur.',
  'mode.convection.name': 'Convection',
  'mode.convection.help': 'L’élément du bas chauffe, avec le ventilateur.',
  'mode.trueConvection.name': 'Convection européenne',
  'mode.trueConvection.help':
    'L’élément arrière chauffe, avec le ventilateur, comme dans les fours européens.',
  'mode.convectionRoast.name': 'Rôtissage par convection',
  'mode.convectionRoast.help': 'Les éléments du haut et du bas chauffent, avec le ventilateur.',
  'mode.broil.name': 'Griller',
  'mode.broil.help':
    'Seul l’élément du haut chauffe, par rayonnement direct ; distinct d’un gril de barbecue.',
  'mode.convectionBroil.name': 'Griller par convection',
  'mode.convectionBroil.help': 'L’élément du haut chauffe, avec le ventilateur.',
  'mode.steam.name': 'Vapeur',
  'mode.steam.help': 'La fonction vapeur du four est en marche, sans chaleur sèche des éléments.',
  'mode.proof.name': 'Levée du pain',
  'mode.proof.help':
    'Un programme à basse chaleur maintient un environnement tiède pour la pousse de la pâte, sans cuisson.',
  'mode.custom.name': 'Autre',
  'mode.custom.help':
    'Mode propre à l’appareil sans correspondance directe dans Flourprint ; son nom d’origine est conservé s’il est connu.',
  'equipment.appliance.oven': 'Four',
  'equipment.appliance.stovetop': 'Plaque de cuisson',
  'equipment.appliance.grill': 'Barbecue',
  'equipment.appliance.steamer': 'Cuiseur vapeur',
  'equipment.appliance.fryer': 'Friteuse',
  'equipment.appliance.proofer': 'Chambre de pousse',
  'equipment.appliance.other': 'Appareil',
  'equipment.label.setpoint': 'Consigne',
  'equipment.label.level': 'Niveau de puissance {value}',
  'equipment.label.levelOf': 'Niveau de puissance {value} sur {of}',
  'equipment.label.fan': 'Ventilation',
  'equipment.fan.on': 'active',
  'equipment.fan.off': 'désactivée',
  'equipment.label.steam': 'Vapeur',
  'equipment.steam.function': 'vapeur du four',
  'equipment.steam.manual': 'manuelle',
  'equipment.label.support': 'Support',
  'equipment.label.vessel': 'Récipient',
  'equipment.cover.open': 'découvert',
  'equipment.cover.covered': 'couvert',
  'equipment.cover.closed': 'fermé',
  'equipment.preheat.hot': 'préchauffé',
  'equipment.preheat.cold': 'démarrage à froid',
  'equipment.label.rack': 'Niveau',
  'equipment.label.load': 'Charge',
  'equipment.label.loadPieces': '{count, plural, one {# pièce} other {# pièces}}',
  'equipment.label.loadMass': '{grams} g',
  'equipment.label.loading': 'Chargement…',
  'equipment.label.missing': 'Élément introuvable',
  'equipment.label.unnamed': 'Équipement sans nom',
  'equipment.label.error': 'Erreur de chargement',

  'recipe.editFermentation': 'Modifier les paramètres de fermentation',
  'recipe.editFermentationFor': 'Modifier les paramètres de fermentation pour {step}',

  'recipe.methodTitle': 'Étapes de préparation',
  'recipe.returnToPlanning': 'Revenir à la planification',
  'planning.editInRecipe': 'Modifier dans la recette',
  'planning.editStepInRecipe': 'Modifier {step} dans la recette',
  'formula.hydration.locked': 'Hydratation verrouillée à {pct}',
  'formula.hydration.unlocked': 'Hydratation déverrouillée',
  'formula.hydration.lockedExplain':
    'Hydratation verrouillée. Déverrouiller avec le cadenas pour modifier.',
  'step.operationType': 'Opération',
  'step.operationBlockedRaw': 'Corriger la saisie avant de changer l’opération.',
  'step.customName': 'Nom de l’étape (facultatif)',
  'step.delete': 'Supprimer l’étape',
  'step.preparationOwnedRefusal':
    'Cette étape appartient à une préparation. Pour la retirer, supprimer le composant dans la section Composants.',
  'process.outcomeResult': 'Résultat : {outcome}',
  'process.outcomeInferredHint': 'Déduit des étapes de préparation.',
  'process.outcomeAuthoredHint': 'Défini pour cette recette.',
  'editor.undo': 'Annuler',
  'editor.redo': 'Rétablir',
  'editor.reset': 'Réinitialiser',
  'editor.resetConfirm':
    'Rétablir les valeurs du début de cette session de modification ? Les modifications non enregistrées et les saisies non confirmées seront perdues.',
  'template.dutchOven.bakeShort': 'Cuisson en cocotte',
  'template.dutchOven.bake':
    'Cuisson en cocotte préchauffée de 6–8 qt à 450 °F (232 °C) : 30 min avec couvercle, puis 22,5 min sans couvercle',
  'template.baguette.bakeShort': 'Cuisson avec buée',
  'template.baguette.bake': 'Cuisson à 450 °F (232 °C) sur pierre ou plaque avec buée',
  'template.ciabatta.bakeShort': 'Cuisson avec buée',
  'template.ciabatta.bake': 'Cuisson à 460 °F (238 °C) avec buée',
  'template.bagels.boilShort': 'Pochage dans l’eau',
  'template.bagels.boil': 'Pocher 30 s par face dans un bain d’eau frémissante par fournées de 3–4',
  'template.bagels.bakeShort': 'Cuisson sur plaque',
  'template.bagels.bake': 'Cuisson sur plaque chemisée de papier sulfurisé à 425 °F (218 °C)',
  'template.focaccia.bakeShort': 'Cuisson en plaque niveau bas',
  'template.focaccia.bake': 'Cuisson en plaque sur la grille inférieure à 475 °F (246 °C)',
  'template.focaccia.bakeFinishShort': 'Finition à chaleur résiduelle',
  'template.focaccia.bakeFinish':
    'Finir hors de la plaque sur la grille inférieure avec le four éteint (chaleur résiduelle)',
  'template.pizzaNeoNeapolitan.bakeShort': 'Cuisson sur plaque d’acier',
  'template.pizzaNeoNeapolitan.bake':
    'Cuisson sur plaque d’acier (Baking Steel) à 500–550 °F (274 °C)',
  'template.pizzaRoman.bakeShort': 'Cuisson four à pizza',
  'template.pizzaRoman.bake': 'Cuisson au four à pizza professionnel (≤350 °C)',
  'template.pizzaNewYork.bakeShort': 'Cuisson sur pierre ou acier',
  'template.pizzaNewYork.bake':
    'Cuisson sur pierre ou plaque d’acier préchauffée à 550 °F (288 °C)',
  'template.pizzaChicago.assemblyShort': 'Garnissage en poêle',
  'template.pizzaChicago.assembly':
    'Disposer par couches dans la poêle : fromages, légumes, viandes et sauce',
  'template.pizzaChicago.bakeShort': 'Cuisson en poêle en fonte',
  'template.pizzaChicago.bake':
    'Cuisson en poêle en fonte à 450 °F (230 °C), couvrir d’une feuille d’aluminium après 20 min',
  'template.pizzaSicilian.finishStretchShort': 'Étirement en plaque',
  'template.pizzaSicilian.finishStretch':
    'Étirer pour remplir la plaque ; répéter le repos de 10–15 min et étirer manuellement si nécessaire',
  'template.pizzaSicilian.parBakeShort': 'Précuisson en plaque',
  'template.pizzaSicilian.parBake':
    'Précuire la pâte sans garniture en plaque 18×13 pouces à 450 °F (232 °C)',
  'template.pizzaSicilian.assemblyShort': 'Garnissage de la pâte',
  'template.pizzaSicilian.assembly':
    'Garnir la pâte précuite avec la sauce, le fromage et les garnitures',
  'template.pizzaSicilian.bakeShort': 'Cuisson de la pizza garnie',
  'template.pizzaSicilian.bake': 'Cuire la pizza garnie à 450 °F (232 °C)',
  'template.pizzaSicilian.coolShort': 'Repos avant découpe',
  'template.pizzaSicilian.cool': 'Laisser reposer avant de découper',
  'template.pizzaTeglia.bakeShort': 'Cuisson au four à sole',
  'template.pizzaTeglia.bake':
    'Cuisson au four à sole électrique (sole 300–320 °C, voûte 200 °C, statique)',
  'template.brioche.mixShort': 'Pétrissage et incorporation du beurre',
  'template.brioche.mix':
    'Pétrir 2 min à basse vitesse, 8 min à vitesse moyenne, puis incorporer le beurre progressivement (température de pâte <=26 °C)',
  'template.brioche.shapeShort': 'Façonnage en boules',
  'template.brioche.shape': 'Façonner en boules de 50 g, disposer 7 boules par moule à cake',
  'template.brioche.proofShort': 'Apprêt en étuve',
  'template.brioche.proof': 'Apprêt en étuve à 28 °C',
  'template.brioche.bakeShort': 'Cuisson chaleur tournante',
  'template.brioche.bake':
    'Dorer avec la dorure à l’œuf, puis cuire au four à chaleur tournante à 180 °C',
  'template.burgerBuns.shapeShort': 'Façonnage des pâtons',
  'template.burgerBuns.shape':
    'Façonner chaque pâton en boule et aplatir à environ 3 pouces (7,5 cm)',
  'template.burgerBuns.bakeShort': 'Cuisson sur plaque beurrée',
  'template.burgerBuns.bake':
    'Badigeonner de la moitié des 43 g de beurre fondu, puis cuire sur plaque à 375 °F (191 °C)',
  'template.burgerBuns.coolShort': 'Beurrage et ressuage',
  'template.burgerBuns.cool':
    'Badigeonner du reste de beurre fondu et laisser refroidir sur grille',
  'template.pullman.mixShort': 'Pétrissage intensif',
  'template.pullman.mix':
    'Pétrir au robot jusqu’au développement complet du réseau glutineux (température de pâte 24–26 °C)',
  'template.pullman.shapeShort': 'Façonnage en bâtard',
  'template.pullman.shape':
    'Façonner en bâtard allongé et placer dans le moule à pain de mie 13x4x4 pouces préparé',
  'template.pullman.proofShort': 'Apprêt en moule ouvert',
  'template.pullman.proof':
    'Laisser lever dans le moule à pain de mie 13x4x4 pouces préparé, sans le couvercle',
  'template.pullman.bakeShort': 'Cuisson en moule fermé',
  'template.pullman.bake':
    'Cuisson à couvert dans le moule à pain de mie 13x4x4 pouces avec couvercle à 200 °C',
  'template.pullman.bakeVentedShort': 'Cuisson démoulée ventilée',
  'template.pullman.bakeVented':
    'Retirer le couvercle, démouler le pain sur une grille et cuire à four ventilé à 200 °C',
  'template.shokupan.tangzhongShort': 'Cuisson du tangzhong',
  'template.shokupan.tangzhong':
    'Cuire 14 g de farine, 43 g d’eau et 43 g de lait à feu doux jusqu’à épaississement',
  'template.shokupan.tangzhongCoolShort': 'Refroidissement du tangzhong',
  'template.shokupan.tangzhongCool':
    'Laisser refroidir le tangzhong jusqu’à ce qu’il soit tiède avant le mélange ; 60 min est une durée indicative modifiable',
  'template.shokupan.shapeShort': 'Façonnage en rouleaux',
  'template.shokupan.shape':
    'Rouler et plier chaque pâton en boudin de 4 pouces, placer dans le moule 9x5 pouces',
  'template.shokupan.bakeShort': 'Cuisson en moule',
  'template.shokupan.bake':
    'Dorer avec la dorure au lait, puis cuire en moule 9x5 pouces à 350 °F (177 °C)',

  ...helpFr,
  'recipe.trash': 'Mettre à la corbeille',
  'recipe.trash.title': 'Mettre à la corbeille ?',
  'recipe.trash.confirm':
    '« {name} » quitte la liste des recettes. Ses versions et ses fournées sont conservées, et la recette peut être restaurée depuis la corbeille.',
  'recipe.trashed':
    'Cette recette est dans la corbeille. La restaurer pour la modifier ou démarrer une fournée.',
  'recipe.deleted': 'Recette supprimée',
  'trash.title': 'Corbeille',
  'trash.hint':
    'Les recettes restent ici jusqu’à leur suppression manuelle. Les fournées déjà faites sont conservées dans tous les cas.',
  'trash.empty': 'La corbeille est vide.',
  'trash.item': 'À la corbeille {when} · version {version}',
  'trash.restore': 'Restaurer',
  'trash.delete': 'Supprimer définitivement',
  'trash.delete.confirm':
    '« {name} » et toutes ses versions seront supprimées pour de bon. Cette action est irréversible. Les fournées déjà faites sont conservées.',
  'trash.emptyAll': 'Vider la corbeille',
  'trash.emptyAll.confirm':
    '{count, plural, one {# recette} other {# recettes}} et toutes leurs versions seront supprimées pour de bon. Cette action est irréversible. Les fournées déjà faites sont conservées.',
  'advanced.trash': 'Corbeille',
  'advanced.trashHint': 'Restaurer une recette ou la supprimer définitivement.',
  'error.RecipeTrashed': 'Cette recette est dans la corbeille. La restaurer d’abord.',
  'error.NotFound': 'Cette recette n’existe plus.',
  'error.AlreadyTrashed': 'Cette recette est déjà dans la corbeille.',
  'error.NotTrashed': 'Cette recette n’est pas dans la corbeille.',
  'recipe.edit': 'Modifier',
  'recipe.plan': 'Planifier',
  'recipe.consultation': 'Lire la recette',
  'recipe.estimateCaption': 'Estimations du modèle provisoire ; elles ne modifient pas la recette.',
  'recipe.doughEstimate': 'Pâte',
  'recipe.estimatedYeast': '{name} : {pct} estimés pour cet horaire',
  'formula.finalMix.temporaryScope': "Aide temporaire de pesée, non enregistrée dans l'historique",
  'app.title': 'Flourprint',
  'recipes.deviceChecks': 'Vérifications de l’appareil',
  'advanced.title': 'Avancé',
  'checks.title': 'Vérifications de l’appareil',
  'checks.intro':
    'Vérifications des spikes #23 et #24. Les lancer sur le téléphone, puis copier le rapport dans l’issue.',
  'checks.environment': 'Environnement',
  'checks.build': 'Build',
  'checks.platform': 'Plateforme',
  'checks.webview': 'WebView',
  'checks.storage': 'Stockage',
  'checks.sqlite': 'SQLite',
  'checks.storeTitle': 'Contrat de stockage',
  'checks.run': 'Lancer les vérifications de stockage',
  'checks.running': 'En cours…',
  'checks.summary': '{passed} vérifications réussies sur {total}',
  'checks.markerTitle': 'Résiste à un arrêt forcé',
  'checks.markerHelp':
    'Écrire un marqueur, forcer l’arrêt de l’app dans les paramètres Android, la rouvrir : la même heure doit s’afficher ici. Idem après l’installation d’un APK plus récent.',
  'checks.markerWrite': 'Écrire un marqueur maintenant',
  'checks.markerLast': 'Dernier marqueur : {time}',
  'checks.markerNone': 'Aucun marqueur pour l’instant.',
  'checks.markerUnavailable': 'Disponible seulement dans l’app installée.',
  'checks.manualTitle': 'À vérifier à la main',
  'checks.manual.offline': 'Mode avion activé, PC éteint : l’app s’ouvre et affiche la recette.',
  'checks.manual.decimal':
    'Dans la formule, taper 2,2 puis 2.2 dans un champ : les deux sont acceptés.',
  'checks.manual.keyboard':
    'Le clavier affiche des chiffres et un séparateur décimal; le champ reste visible.',
  'checks.manual.draft':
    'Modifier une valeur, revenir à la liste, rouvrir la recette : la modification est toujours là.',
  'checks.manual.theme': 'Le texte est lisible en mode clair et en mode sombre.',
  'checks.copy': 'Copier le rapport',
  'checks.copied': 'Rapport copié.',
  'state.recipeMissing': 'Cette recette n’existe pas.',
  'state.bakeMissing': 'Cette fournée n’existe pas.',
  'state.storageRead': 'Les données n’ont pas pu être lues. Fermer puis rouvrir l’app.',
  'state.storageWrite': 'Rien n’a pu être enregistré. Les modifications sont gardées; réessayer.',
  'state.storageInit': 'Flourprint n’a pas pu ouvrir ses données. Fermer puis rouvrir l’app.',
  'state.sessionOnly':
    'Aperçu navigateur : les données restent dans cet onglet et disparaissent au rechargement.',
  'state.browserKept':
    'Aperçu navigateur : les données sont gardées dans ce navigateur, sur cet appareil seulement.',
  'state.browserUnreadable':
    'Les données gardées dans ce navigateur n’ont pas pu être lues : elles sont mises de côté et l’app démarre vide.',
  'advanced.browserErase': 'Effacer les données du navigateur',
  'advanced.browserEraseHint':
    'Recettes, brouillons, farines et fournées gardés par l’aperçu navigateur.',
  'advanced.browserEraseConfirm':
    'Tout ce que l’aperçu navigateur a gardé est effacé et la page se recharge vide. Copier les données avant pour les garder.',
  'state.retry': 'Réessayer',
  'recipes.title': 'Recettes',
  'recipes.empty':
    'Aucune recette pour l’instant. Toucher + pour partir d’un modèle ou d’une recette vierge.',
  'recipes.card.dough': 'Pâte',
  'recipes.card.hydration': 'Hydratation',
  'recipes.card.duration': 'Durée',
  'recipes.card.lastBaked': 'Dernière cuisson {when}',
  'recipes.card.neverBaked': 'Jamais cuite',
  'recipes.activeBake': 'Préparation en cours · {recipe}',
  'recipes.activeBakeCard': 'Préparation en cours',
  'recipes.resumeBake': 'Reprendre la préparation →',
  'recipe.menu': 'Menu de la recette',
  'recipe.nameLabel': 'Nom de la recette',
  'editor.leaveTitle': 'Quitter la modification ?',
  'editor.leaveDraft':
    'Le brouillon est conservé. Enregistrer la recette avant de démarrer une préparation.',
  'editor.leaveInvalid':
    'Le texte non accepté ne peut pas être enregistré. Quitter en conservant le dernier brouillon accepté et abandonner ce texte ?',
  'editor.keepDraft': 'Conserver le brouillon et quitter',
  'outcome.pieces': 'Pâtons crus conservés (facultatif)',
  'recipes.card.cookingUnknown': 'Cuisson non renseignée',
  'recipe.editAction': 'Modifier la recette',
  'recipe.trashAction': 'Déplacer dans la corbeille',
  'recipe.startAction': 'Démarrer la préparation',
  'recipe.resumeAction': 'Reprendre la préparation',
  'recipe.tab.comp': 'Composition',
  'recipe.tab.parts': 'Composants',
  'recipe.tab.steps': 'Étapes',
  'recipe.tab.res': 'Résultat',
  'editor.menu': 'Menu de l’éditeur',
  'editor.check': 'Vérifier la recette',
  'editor.review': 'Examiner les modifications',
  'editor.discard': 'Abandonner les modifications',
  'editor.save': 'Enregistrer la recette',
  'editor.draftUnsaved': 'Modifications non enregistrées',
  'editor.draftClean': 'Recette v{version}',
  'prep.progressPct': '{pct} % du temps prévu écoulé',
  'prep.sessionStarted': ' · Début {time}',
  'prep.readyMark': 'Prête à façonner',
  'prep.plus15': '+15 min',
  'prep.duration': 'Durée',
  'prep.note': 'Note',
  'formula.column.basisFlour': '% de la farine',
  'formula.column.basisBaker': '% du boulanger',
  'formula.column.basisGrams': 'Poids',
  'component.ratioLabel': 'Ratio farine:liquide',
  'component.ratioRejected':
    'Ce ratio ne peut pas être appliqué. Vérifier la farine, l’eau et les ingrédients alloués au composant.',
  'component.ratioUnknown': 'Ratio indisponible sans farine dans le composant',
  'component.ratioBasisDesc':
    'Le ratio compte l’eau pesée et les ingrédients alloués, incluant la masse entière du lait. L’hydratation compte leur teneur en eau.',
  'bake.headerTitle': 'Préparation',
  'bake.overflowActions': 'Actions de préparation',
  'bake.finishRaw': 'Terminer la préparation (pâte crue)',
  'bake.timeNotBiological': 'La durée prévue ne confirme pas l’état de la pâte.',
  'bake.completedSteps': 'Étapes terminées',
  'recipe.overflowActions': 'Actions sur la recette',
  'editor.overflowActions': 'Menu édition',
  'bakes.title': 'Préparations en cours',
  'bakes.empty': 'Aucune préparation en cours. En démarrer une depuis une recette.',
  'bakes.open': 'Ouvrir',
  'bakes.unreadable':
    '{count, plural, one {# fournée en cours ne peut pas être lue par cette version} other {# fournées en cours ne peuvent pas être lues par cette version}}.',
  'bakes.alert.end': '{time} · fin : {step}',
  'bakes.alert.start': '{time} · commencer : {step}',
  'recipes.allBakes': 'Suivre les {count} fournées',
  'recipe.version': 'Version {version}',
  'recipe.tab.formula': 'Recette',
  'recipe.tab.process': 'Planification',
  'recipe.tab.history': 'Historique',
  'recipe.bake': 'Démarrer',
  'recipe.baking': 'En cours',
  'draft.unsaved': 'Modifications non enregistrées',
  'draft.invalid': 'Corriger les champs invalides',
  'draft.invalidPreview':
    'Les valeurs affichées sont celles déjà acceptées. Corriger les champs invalides avant de démarrer.',
  'draft.save': 'Enregistrer v{version}',
  'draft.saved': 'Version {version} enregistrée.',
  'draft.discard': 'Abandonner les modifications',
  'guide.title': 'À propos de ce style',
  'guide.unverified':
    'Fourchettes typiques tirées de références de boulangerie, pas encore vérifiées dans les sources complètes.',
  'guide.ranges': 'Valeurs de référence de la recette retenue',
  'guide.basis': 'Source et choix éditoriaux (anglais)',
  'guide.referenceNote':
    'Ces valeurs décrivent la variante publiée retenue, sans constituer un consensus ni une prédiction.',
  'guide.use': 'Partir de ce modèle',
  'guide.key.hydration_pct': 'Eau',
  'guide.key.hydration_pct_high': 'Eau (famille très hydratée)',
  'guide.key.salt_pct': 'Sel',
  'guide.key.hydration_max_pct': 'Eau pure (maximum publié)',
  'guide.key.butter_pct': 'Beurre (masse de l’ingrédient)',
  'guide.key.oil_pct': 'Huile dans la pâte',
  'guide.key.added_fats_pct': 'Huile et beurre (masse des ingrédients)',
  'guide.key.non_diastatic_malt_pct': 'Malt en poudre non diastatique',
  'guide.key.cornmeal_pct': 'Semoule de maïs dans le total de farine',
  'guide.key.cold_retard_min_h': 'Repos au froid (minimum publié)',
  'guide.key.cold_retard_max_h': 'Délai d’utilisation au froid (maximum publié)',
  'guide.key.bake_max_c': 'Four professionnel (maximum du guide)',
  'guide.key.simmer_per_side_s': 'Pochage par côté, par bagel',
  'guide.key.sugar_pct': 'Sucre',
  'guide.key.fat_pct': 'Gras',
  'guide.key.fat_pct_high': 'Gras (famille très hydratée)',
  'guide.key.egg_pct': 'Œufs',
  'guide.key.milk_pct': 'Lait',
  'guide.key.water_pct': 'Eau en plus des œufs',
  'guide.key.liquid_pct': 'Eau et lait',
  'guide.key.yeast_instant_pct': 'Levure instantanée',
  'guide.key.yeast_active_dry_pct': 'Levure sèche active',
  'guide.key.preferment_pct': 'Farine dans le préferment',
  'guide.key.biga_hydration_pct': 'Hydratation de la biga',
  'guide.key.scald_flour_pct': 'Farine dans le tangzhong',
  'guide.key.malt_syrup_pct': 'Sirop de malt',
  'guide.key.cornmeal_or_semolina_pct': 'Semoule de maïs ou de blé',
  'guide.key.poolish_h': 'Poolish',
  'guide.key.biga_h': 'Biga',
  'guide.key.bulk_h': 'Pointage',
  'guide.key.cold_retard_h': 'Fermentation au froid',
  'guide.key.proof_h': 'Apprêt',
  'guide.key.boil_s': 'Ébullition',
  'guide.key.bake_c': 'Four',
  'guide.key.bake_min': 'Cuisson',
  'guide.key.ball_g': 'Pâton',
  'guide.key.dough_g_13in': 'Pâte pour un moule de 13 po',
  'guide.key.dough_g_half_sheet': 'Pâte pour une demi-plaque',
  'guide.key.flour_w': 'Force de la farine (W)',
  'template.baguette.traits':
    'Un pain long et mince, de farine, eau, sel et levure, souvent sur poolish, cuit très chaud avec buée : croûte fine et croustillante, mie ouverte.',
  'template.boule-dutch-oven.traits':
    'Un pain rond maigre cuit dans une cocotte préchauffée et couverte qui garde la vapeur, puis découverte pour dorer ; plus hydraté qu’une baguette, fermentation lente avec peu de levure.',
  'template.ciabatta.traits':
    'Un pain plat italien en forme de pantoufle, d’une pâte très hydratée sur biga ferme, à peine façonné : grosse mie irrégulière, croûte fine.',
  'template.bagels.traits':
    'Des bagels à préparer le jour même, avec du malt en poudre non diastatique : des anneaux pochés brièvement de chaque côté, puis cuits sur plaque. Cette variante ne comporte pas de repos au froid une nuit.',
  'template.focaccia.traits':
    'Un pain plat très hydraté, plié puis alvéolé dans un moule carré. L’huile de la pâte est distincte de celle du moule et de la surface ; la finition sur grille utilise le four éteint.',
  'template.brioche.traits':
    'Un pain français riche en œufs et en beurre, avec de la levure fraîche. Le beurre est incorporé après le pétrissage initial ; cette variante retient un repos au froid d’une heure avant le façonnage.',
  'template.burger-buns.traits':
    'Des petits pains ronds et moelleux enrichis d’œuf, de beurre et de sucre, avec une pâte à base d’eau, sans lait. Le beurre de finition est distinct du beurre dans la pâte.',
  'template.pullman.traits':
    'Un pain de mie carré cuit dans un moule couvert de 13 po. Cette pâte à base d’eau contient du beurre, du sucre et du sirop de malt, sans lait ni œuf.',
  'template.shokupan.traits':
    'Un pain au lait japonais enrichi avec un tangzhong cuit. Cette variante utilise de l’eau et du lait dans la pâte cuite, pour un rapport liquide total sur farine du tangzhong d’environ 6,1:1.',
  'template.pizza-neo-neapolitan.traits':
    'Une pizza au four maison sur acier : un pointage de 24 heures à température ambiante puis 48 heures au froid, suivi du boulage et d’un apprêt distinct. Une adaptation domestique, sans certification AVPN.',
  'template.pizza-roman.traits':
    'Une pizza romaine ronde, mince et croustillante, selon la formule professionnelle directe retenue : de petits pâtons, sans huile dans la pâte, avec un four à pizza très chaud.',
  'template.pizza-teglia.traits':
    'La pizza romaine en plaque : une pâte très hydratée de farine forte, maturée au froid un jour ou plus, étirée dans une plaque huilée : mie aérée, dessous croustillant.',
  'template.pizza-new-york.traits':
    'Une grande pizza mince et pliable, de farine forte avec un peu d’huile et souvent de sucre, fermentée au froid un à trois jours.',
  'template.pizza-sicilian.traits':
    'Une pizza épaisse et aérée, rectangulaire, cuite dans une plaque bien huilée pour que le dessous frise et croustille.',
  'template.pizza-chicago.traits':
    'Une variante domestique de deep dish avec de la semoule de maïs, de l’huile d’olive et du beurre dans la pâte, cuite en poêle de fonte. La semoule entre dans le total de farine ; aucun apprêt distinct n’est prescrit.',
  'draft.rename': 'Renommer',
  'draft.delete': 'Supprimer le brouillon',
  'draft.deleteConfirm': 'Supprimer ce brouillon ? C’est définitif.',
  'draft.discardConfirm': 'Abandonner les modifications non enregistrées ?',
  'draft.newRecipe': 'Brouillon, pas encore enregistré',
  'draft.saveRecipe': 'Enregistrer comme recette',
  'draft.blankName': 'Recette',
  'draft.clean': 'À jour',
  'draft.saveShort': 'Enregistrer',
  'draft.reviewShort': 'Examiner',
  'draft.discardShort': 'Abandonner',
  'draft.deleteShort': 'Supprimer',
  'draft.reviewNew': 'Nouvelle recette : aucune version enregistrée à comparer.',
  'draft.review': 'Examiner les modifications',
  'draft.reviewTitle': 'Modifications du brouillon',
  'draft.reviewFormula': 'Modifications de la formule',
  'draft.reviewProcess': 'Modifications du processus',
  'draft.reviewNoFormula': 'Aucune modification de la formule.',
  'draft.reviewProcessModified': 'Paramètres du processus modifiés.',
  'draft.reviewProcessClean': 'Aucune modification du processus.',
  'check.title': 'Vérification de la recette',
  'check.none': 'Rien à signaler : la recette peut être enregistrée et démarrée.',
  'check.blocking': 'À corriger avant d’enregistrer ou de démarrer',
  'check.warnings': 'À vérifier (ne bloque pas)',
  'check.button': 'Vérifier',
  'check.buttonCount': '{count, plural, one {# point à vérifier} other {# points à vérifier}}',
  'check.no_steps': 'Le processus n’a aucune étape.',
  'check.salt_missing': 'Pas de sel : l’ajouter avec « Ajouter un ingrédient ».',
  'check.salt_outside_1_to_3_5': 'Sel à {pct} : inhabituel (1 à 3,5 % attendu).',
  'check.water_outside_45_to_90': 'Hydratation à {pct} : inhabituelle (45 à 90 %).',
  'check.no_leavening': 'Ni levure ni préferment : la pâte ne lèvera pas.',
  'check.no_mix_step': 'Aucune étape de pétrissage.',
  'check.no_bake_step': 'Aucune étape de cuisson.',
  'check.step_without_duration': '{step} n’a pas de durée.',
  'recipes.drafts': 'Brouillons',
  'recipes.draftEdits': 'Modifications non enregistrées',
  'new.title': 'Nouvelle recette',
  'new.blank': 'Recette vierge',
  'new.blankHint': 'Farine, eau, sel et levure, pour partir de zéro.',
  'new.templates': 'Partir d’un modèle',
  'new.templatesHint':
    'Proportions typiques du style : un point de départ, pas une recette testée.',
  'new.category.bread': 'Pains',
  'new.category.enriched': 'Pâtes enrichies',
  'new.category.pan': 'Pains en moule',
  'new.category.pizza': 'Pizzas',
  'new.category.flatbread': 'Pains plats',
  'template.baguette': 'Baguette',
  'template.boule-dutch-oven': 'Pain boule en cocotte',
  'template.brioche': 'Brioche',
  'template.burger-buns': 'Pains hamburger',
  'template.pullman': 'Pain Pullman',
  'template.shokupan': 'Shokupan',
  'template.ciabatta': 'Ciabatta',
  'template.bagels': 'Bagels',
  'template.focaccia': 'Focaccia',
  'template.pizza-neo-neapolitan': 'Pizza néo-napolitaine',
  'template.pizza-roman': 'Pizza romaine (tonda)',
  'template.pizza-new-york': 'Pizza new-yorkaise',
  'template.pizza-chicago': 'Pizza Chicago (deep dish)',
  'template.pizza-sicilian': 'Pizza sicilienne',
  'template.pizza-teglia': 'Pizza en teglia (romaine en plaque)',
  'name.tipo-00-flour': 'Farine tipo 00',
  'name.cornmeal': 'Semoule de maïs',
  'name.barley-malt-syrup': 'Sirop de malt d’orge',
  'name.biga': 'biga',
  'operation.boil': 'Ébullition',
  'formula.quantity': 'Taille de la recette',
  'formula.anchor.flourTotal': 'Farine',
  'formula.anchor.doughWeight': 'Pâte',
  'formula.anchor.pieces': 'Pâtons',
  'formula.anchorField.flourTotal': 'Farine totale',
  'formula.anchorField.doughWeight': 'Poids de pâte',
  'formula.anchorField.count': 'Nombre de pâtons',
  'formula.anchorField.gramsEach': 'Poids d’un pâton',
  'formula.stat.dough': 'Pâte',
  'formula.stat.hydration': 'Hydratation',
  'formula.stat.pureWater': 'Eau ajoutée',
  'formula.stat.totalWaterValue': '{pct}',
  'formula.stat.totalWaterEstimate': '≈ {pct}',
  'formula.editRule':
    'Changer des grammes garde les autres grammes ; changer un % garde la taille de la recette.',
  'formula.component.flour': '{grams} de farine',
  'formula.component.water': '{grams} d’eau',
  'formula.component.allocation': '{grams} {name}',
  'formula.component.loss': '−{grams} d’évaporation',
  'formula.water.incomplete': 'Hydratation incomplète : eau inconnue dans {ingredients}.',
  'formula.group.flours': 'Farines',
  'formula.group.ingredients': 'Ingrédients',
  'formula.column.flourPct': 'Part',
  'formula.column.bakerPct': '% boulanger',
  'formula.column.grams': 'Poids',
  'formula.preparations': 'Préparations',
  'formula.prep.flourPct': '{pct} de la farine',
  'formula.prep.hydration': '{pct} d’hydratation',
  'formula.prep.allocation': '{name} {pct}',
  'formula.finalMix': 'Ingrédients',
  'formula.finalMix.progress': '{done} sur {total}',
  'explain.title': 'D’où vient l’eau',
  'explain.pureWater': '{name} (eau ajoutée)',
  'explain.ingredient': '{name} ({status, select, known {connue} estimated {estimée} other {}})',
  'explain.loss': 'Perte dans {name}',
  'explain.unknown': '{name} : inconnue',
  'explain.close': 'Fermer',
  'field.invalidNumber': 'Entrer un nombre.',
  'field.invalidDuration': 'Entrer une durée comme 45 min ou 1 h 30.',
  'process.mode.startAt': 'Commencer à',
  'process.mode.readyBy': 'Prêt pour',
  'process.when.startAt': 'Début',
  'process.when.now': 'Maintenant',
  'process.when.chosen': 'À une heure',
  'process.when.time': 'Heure de début',
  'process.startsNow':
    'La préparation démarre au moment du toucher : ses minuteries partent de là. Les heures ci-dessus servent à planifier.',
  'process.when.readyBy': 'Fin : {step}',
  'process.summary.startAt': '{step} · fin {when}',
  'process.summary.readyBy': '{step} · début {when}',
  'process.unknownDay': 'Heure inconnue',
  'process.target': 'Prêt à la fin de',
  'process.alap': 'Le plus tard possible',
  'process.after': 'Après {steps}',
  'process.unknown': 'Heure inconnue tant que {steps} n’est pas terminé',
  'process.duration': 'Durée : {step}',
  'process.openBake': 'Voir la préparation en cours',
  'process.startAnother': 'Démarrer une autre préparation',
  'process.saveFirst': 'Enregistrer les modifications avant de démarrer une préparation.',
  'operation.preferment': 'Préferment',
  'operation.scald': 'Échaudage',
  'operation.autolyse': 'Autolyse',
  'operation.fermentolyse': 'Fermentolyse',
  'operation.mix': 'Pétrissage',
  'operation.fold': 'Rabat',
  'operation.bulk': 'Pointage',
  'operation.retard': 'Retard au froid',
  'operation.divide': 'Division',
  'operation.preshape': 'Préfaçonnage',
  'operation.rest': 'Détente',
  'operation.shape': 'Façonnage',
  'operation.proof': 'Apprêt',
  'operation.preheat': 'Préchauffage',
  'operation.bake': 'Cuisson',
  'operation.cool': 'Ressuage',
  'operation.degas': 'Dégazage',
  'process.addStep': '+ Ajouter une étape',
  'process.addStep.after': 'Après',
  'process.addStep.operation': 'Nouvelle étape',
  'process.addStep.confirm': 'Ajouter',
  'process.addStep.cancel': 'Annuler',
  'process.addStep.hint': 'La nouvelle étape n’a pas encore de durée : l’indiquer dans la liste.',
  'process.editStep': 'Modifier l’étape {step}',
  'technique.title': 'Technique',
  'technique.close': 'Fermer',
  'technique.method': 'Méthode',
  'technique.none': 'Aucune',
  'technique.method.hand': 'À la main',
  'technique.method.slapAndFold': 'Frapper-plier',
  'technique.method.rubaud': 'Méthode Rubaud',
  'technique.method.standMixer': 'Batteur sur socle',
  'technique.method.spiral': 'Spirale',
  'technique.method.foodProcessor': 'Robot culinaire',
  'technique.method.noKnead': 'Sans pétrissage',
  'technique.method.stretchAndFold': 'Étirer-rabattre',
  'technique.method.coilFold': 'Rabat en bobine',
  'technique.method.lamination': 'Laminage',
  'technique.phases': 'Phases de pétrissage',
  'technique.phases.hint': 'La durée de l’étape est la somme des phases.',
  'technique.phase.speed': 'Vitesse de la phase {index}',
  'technique.phase.duration': 'Durée de la phase {index}',
  'technique.phase.remove': 'Retirer la phase {index}',
  'technique.phase.add': '+ Ajouter une phase',
  'technique.phase.summary': '{duration} {speed}',
  'technique.speed.low': 'lent',
  'technique.speed.medium': 'moyen',
  'technique.speed.high': 'rapide',
  'technique.folds': 'Rabats pendant l’étape',
  'technique.folds.count': 'Nombre de séries',
  'technique.folds.interval': 'Intervalle entre les séries',
  'technique.folds.style': 'Style',
  'technique.folds.remove': 'Retirer les rabats',
  'technique.folds.hint':
    'La série 1 est due un intervalle après le début de l’étape, la série 2 deux intervalles après, et ainsi de suite.',
  'technique.folds.summary':
    '{count, plural, one {# série de rabats} other {# séries de rabats}} / {interval}',
  'technique.endCheck': 'Fin de l’étape',
  'technique.endCheck.windowpane': 'Test de la fenêtre',
  'technique.endCheck.doughTemperature': 'Température de pâte',
  'technique.endCheck.feel': 'Toucher',
  'technique.endCheck.time': 'Temps',
  'technique.endCheck.other': 'Autre',
  'technique.endCheck.summary': 'fin : {check}',
  'technique.removeStep': 'Retirer cette étape',
  'technique.removeConfirm':
    'Retirer {step} ? Les étapes qui la suivaient suivent alors celles qui la précédaient.',
  'technique.preparationStep':
    'Cette étape appartient à une préparation : retirer la préparation dans l’onglet Formule.',
  'bake.fold.title': 'Rabats : {step}',
  'bake.fold.next': 'Série {set}/{count} · dans {time}',
  'bake.fold.overdue': 'Série {set}/{count} · en retard de {time}',
  'bake.fold.allDone': 'Les {count} séries sont faites',
  'bake.fold.done': 'Rabat fait',
  'bake.fold.pick': 'Quelle série a été faite ?',
  'bake.fold.set': 'Série {set}',
  'bake.log.fold': '{time} · Rabats {set}/{count} faits ({step})',
  'notify.fold.title': 'Rabats : série {set}/{count}',
  'notify.fold.body': '{recipe} · {step}, série {set} prévue',
  'check.folds_exceed_step': 'Les rabats de {step} durent plus longtemps que l’étape.',
  'history.empty':
    'Aucune préparation pour l’instant. En démarrer une avec le bouton Démarrer la préparation.',
  'history.item': '{date} · version {version}',
  'history.status.notStarted': 'Pas commencée',
  'history.status.active': 'En cours',
  'history.status.finished': 'Terminée',
  'history.status.abandoned': 'Abandonnée',
  'bake.now': 'Maintenant',
  'bake.upNext': 'À venir',
  'bake.position': 'Étape {index} sur {total}',
  'bake.left': 'restantes',
  'bake.over': 'de dépassement',
  'bake.planned': 'prévues',
  'bake.stepPlannedDuration': 'Prévu : {duration}',
  'bake.startedAt': 'Commencé à {time}',
  'bake.endsAt': 'Fin prévue {time}',
  'bake.startsAt': 'Début prévu {time}',
  'bake.noDuration': 'Pas de durée prévue',
  'bake.done': '{step} terminé',
  'bake.start': 'Commencer : {step}',
  'bake.plus15': '+15 min',
  'bake.adjust': 'Durée',
  'bake.note': 'Note',
  'bake.doughReady': 'Pâte prête',
  'bake.readyToShape': 'Prête à façonner',
  'bake.readyToBake': 'Prête à cuire',
  'bake.log.ready': '{time} · {label} ({details}) {text}',
  'state.writeBusy':
    'Une autre modification est en cours d’enregistrement. Réessayer dans un instant.',
  'bake.doughReadyTitle': 'Pâte prête : sur quoi se fier ?',
  'bake.doughReady.poke': 'Test du doigt',
  'bake.doughReady.feel': 'Toucher',
  'bake.doughReady.time': 'Temps',
  'bake.doughReady.other': 'Autre',
  'bake.next': 'Ensuite',
  'bake.dueNow': 'À faire aussi maintenant',
  'bake.startShort': 'Commencer',
  'bake.doneShort': 'Terminé',
  'bake.alsoRunning': 'Aussi en cours',
  'bake.allDone': 'Toutes les étapes sont terminées.',
  'bake.finish': 'Terminer la fournée',
  'bake.abandon': 'Abandonner la préparation',
  'bake.abandonConfirm': 'Abandonner cette préparation ? Son journal est conservé.',
  'bake.cancel': 'Annuler',
  'bake.save': 'Enregistrer',
  'bake.adjustTitle': 'Durée prévue : {step}',
  'bake.noteTitle': 'Note',
  'bake.notePlaceholder': 'Température de pâte, toucher, pousse…',
  'bake.log': 'Journal',
  'bake.log.started': '{time} · {step} commencé',
  'bake.log.completed': '{time} · {step} terminé',
  'note.step': 'Étape',
  'note.wholeBake': 'Toute la fournée',
  'note.category': 'Sujet',
  'note.category.fermentation': 'Fermentation',
  'note.category.handling': 'Manipulation',
  'note.category.volume': 'Volume',
  'note.category.crust': 'Croûte',
  'note.category.crumb': 'Mie',
  'note.category.flavor': 'Goût',
  'note.category.other': 'Autre',
  'note.rating': 'Appréciation',
  'bake.addNote': 'Ajouter une note',
  'bake.log.noteOn': '{time} · {step} : {text}',
  'bake.log.note': '{time} · {text}',
  'bake.finished': 'Préparation terminée',
  'bake.abandoned': 'Préparation abandonnée',
  'error.InvalidProcess': 'Ce processus n’est pas valide.',
  'error.DependencyCycle': 'Ces étapes dépendent les unes des autres en boucle.',
  'error.unknown': 'Une erreur est survenue ({code}).',
  'error.FlourAllocationExceeded':
    'Les préparations demandent {required} de {flour}, mais la formule n’en contient que {available}.',
  'error.WaterAllocationExceeded':
    'Les préparations demandent {required} d’eau, mais la formule n’en contient que {available}.',
  'error.IncompatibleConstraint': 'Cette modification est impossible avec la quantité actuelle.',
  'error.InvalidFormula': 'Cette valeur n’est pas valide.',
  'error.HeadConflict': 'La recette a changé entre-temps. La recharger avant d’enregistrer.',
  'error.DependenciesNotCompleted': 'Terminer d’abord les étapes précédentes.',
  'error.StepAlreadyCompleted': 'Cette étape est déjà terminée.',
  'error.StepAlreadyStarted': 'Cette étape a déjà commencé.',
  'error.BakeNotActive': 'Cette fournée n’est pas en cours.',
  'operation.steam': 'Cuisson vapeur',
  'operation.fry': 'Friture',
  'operation.panCook': 'Cuisson à la poêle',
  'operation.grill': 'Barbecue',
  'outcome.cooked': 'Cuit',
  'outcome.dough': 'Pâte crue',
  'outcome.cookedAndDough': 'Cuit et pâte crue',
  'outcome.withDough': '{result}, {dough}',
  'outcome.dough.frozen': 'congelée',
  'outcome.dough.refrigerated': 'au frigo',
  'outcome.dough.given': 'donnée',
  'outcome.dough.sold': 'vendue',
  'outcome.dough.other': 'autre',
  'outcome.unknown': 'Non enregistré',
  'outcome.auto': 'Automatique : {outcome}',
  'outcome.pick': 'Résultat',
  'bake.finishAs': 'Terminer · {outcome}',
  'bake.finishOther': 'Autre résultat',
  'bake.finishNow': 'Terminer maintenant…',
  'outcome.pickDough': 'Devenir de la pâte crue',
  'outcome.dough.unknown': 'Non précisé',
  'bake.outcome': 'Résultat : {outcome}',
  'bake.outcome.change': 'Modifier le résultat',
  'bake.reopen': 'Rouvrir',
  'bake.reopenConfirm':
    'La fournée redevient active pour enregistrer d’autres étapes. Sa fin précédente reste dans l’historique.',
  'process.intendedOutcome': 'Fin prévue',
  'process.intendedOutcomeHint':
    'Enregistrée en terminant la fournée, sauf si un autre résultat est choisi à ce moment.',
  'stats.results': 'Résultats des fournées terminées',
  'stats.cookingStages': 'Étapes de cuisson faites',
  'error.NotFinished': 'Seule une fournée terminée a un résultat à modifier.',
  'error.BakeActive': 'Cette fournée est encore en cours.',
  'error.VersionConflict': 'La fournée a changé entre-temps. Elle a été rechargée.',
  'name.bread-flour': 'Farine à pain',
  'name.whole-wheat': 'Farine de blé entier',
  'name.water': 'Eau',
  'name.salt': 'Sel',
  'name.instant-yeast': 'Levure instantanée',
  'name.fresh-yeast': 'Levure fraîche',
  'name.yeast': 'Levure (forme non précisée)',
  'name.milk-powder': 'Lait en poudre',
  'name.non-diastatic-malt-powder': 'Malt en poudre non diastatique',
  'name.poolish': 'poolish',
  'name.tangzhong': 'tangzhong',
  'name.flour': 'Farine',
  'name.spelt-flour': 'Farine d’épeautre',
  'name.brown-flour': 'Farine brune',
  'name.active-dry-yeast': 'Levure sèche active',
  'name.milk': 'Lait',
  'name.butter': 'Beurre',
  'name.egg': 'Œuf',
  'name.honey': 'Miel',
  'name.sugar': 'Sucre',
  'name.olive-oil': 'Huile d’olive',
  'name.sour-cream': 'Crème sure',
  'name.cider-vinegar': 'Vinaigre de cidre',
  'name.diastatic-malt': 'Malt diastasique',
  'style.tangzhong': 'tangzhong',
  'style.yudane': 'yudane',
  'style.scald': 'échaudage',
  'style.poolish': 'poolish',
  'style.biga': 'biga',
  'style.preferment': 'préferment',
  'style.soaker': 'trempage',
  'style.custom': 'préparation',
  'formula.stat.fat': 'Matières grasses',
  'formula.stat.sugar': 'Sucres',
  'formula.enrichment.incomplete': 'Inconnu pour : {ingredients}.',
  'advanced.feedback': 'Données pour analyse',
  'advanced.copyAll': 'Copier toutes mes données',
  'advanced.copyAllHint':
    'Recettes, fournées, notes et minutages en un seul bloc, à coller à une personne ou à un agent IA.',
  'advanced.saveAll': 'Enregistrer toutes mes données dans un fichier',
  'advanced.exporting': 'Préparation…',
  'advanced.copied':
    'Copié : {size} Ko, {bakes, plural, =0 {aucune fournée} one {# fournée} other {# fournées}}.',
  'advanced.saved':
    'Enregistré : {size} Ko, {bakes, plural, =0 {aucune fournée} one {# fournée} other {# fournées}}.',
  'advanced.copyFailed': 'La copie a échoué.',
  'advanced.exportFailed': 'Les données n’ont pas pu être lues.',
  'notify.channel': 'Rappels de fournée',
  'notify.end.title': '{step} : c’est l’heure',
  'notify.end.body': '{recipe} · fin prévue de {step}',
  'notify.start.title': 'Commencer : {step}',
  'notify.start.body': '{recipe} · début prévu de {step}',
  'advanced.notifications': 'Rappels',
  'advanced.notify.granted': 'Les rappels sont activés.',
  'advanced.notify.denied': 'Les rappels sont bloqués dans les réglages du système.',
  'advanced.notify.prompt': 'Les rappels ne sont pas encore autorisés.',
  'advanced.notify.none': 'Cet appareil ne peut pas afficher de rappels.',
  'advanced.notify.web': 'Les rappels s’affichent tant que l’app est ouverte.',
  'advanced.notify.allow': 'Autoriser les rappels',
  'advanced.notify.exact': 'Autoriser les rappels à l’heure',
  'advanced.notify.exactHint':
    'Sans cela, Android peut livrer les rappels avec plusieurs minutes de retard.',
  'advanced.recipes': 'Recettes',
  'advanced.devUpdate': 'Charger la dernière version dev',
  'advanced.devUpdate.hint': 'Version en cours : {label}. Versions dev seulement.',
  'advanced.devUpdate.checking': 'Vérification de la version déployée…',
  'advanced.devUpdate.current': '{label} est déjà la dernière version.',
  'advanced.devUpdate.loading': 'Chargement de {label}…',
  'advanced.devUpdate.unreachable': 'Site injoignable. Vérifier la connexion.',
  'advanced.devUpdate.saveFailed':
    'Une version plus récente est disponible, mais les données n’ont pas pu être enregistrées d’abord : rien n’a été rechargé.',
  'advanced.sample': 'Charger les recettes d’exemple',
  'advanced.sampleHint':
    'Versions de développement seulement : ajoute Poolish + tangzhong pour essayer l’app. Une recette déjà présente est conservée.',
  'advanced.import': 'Importer des recettes…',
  'advanced.importHint':
    'Ajouter les recettes d’un fichier de recettes Flourprint. Les recettes déjà présentes restent telles quelles.',
  'advanced.importing': 'Import en cours…',
  'advanced.imported':
    '{imported, plural, =0 {Aucune nouvelle recette} one {# recette importée} other {# recettes importées}}{skipped, plural, =0 {} one {, # déjà présente} other {, # déjà présentes}}.',
  'advanced.importUnreadable': 'Ce fichier ne peut pas être lu comme du JSON.',
  'error.InvalidRecipeFile': 'Ce n’est pas un fichier de recettes Flourprint valide.',
  'error.UnsupportedDocumentVersion': 'Ce fichier vient d’une version plus récente de Flourprint.',
  'error.NotInFamily': 'Cette recette n’est pas une variante de cette famille.',
  'error.FamilyNotFound': 'Cette famille n’existe plus.',
  'error.RecipeNotFound': 'Cette recette n’existe plus.',
  'family.variants': '{count, plural, one {# variante} other {# variantes}}',
  'family.title': 'Variantes de {family}',
  'family.default': 'Affichée par défaut',
  'family.pin': 'Afficher cette variante par défaut',
  'family.unpin': 'Afficher la plus récente par défaut',
  'family.mostRecent': 'La plus récente est affichée par défaut',
  'formula.lock.follows': 'Suit l’hydratation verrouillée',
  'formula.hydration.lock': 'Verrouiller l’hydratation à {pct}',
  'formula.hydration.unlock': 'Déverrouiller l’hydratation',
  'formula.hydration.breakdown': 'D’où vient l’eau',
  'formula.removeNamed': 'Retirer : {name}',
  'formula.remove': 'Retirer',
  'formula.add.ingredient': '+ Ajouter un ingrédient',
  'formula.add.flour': '+ Ajouter une farine',
  'formula.add.flourHint':
    'Elle est ajoutée à 0 % : régler les parts pour que les farines fassent 100 %.',
  'formula.unresolved':
    'Les quantités ne peuvent pas encore être calculées : vérifier la recette pour voir pourquoi.',
  'formula.add.other': 'Autre…',
  'formula.add.name': 'Nom',
  'formula.add.water': 'Teneur en eau en % (vide si inconnue)',
  'formula.add.hint': 'Il est ajouté à 0 % : régler sa quantité dans la liste.',
  'formula.add.confirm': 'Ajouter',
  'formula.add.cancel': 'Annuler',
  'formula.add.poolish': '+ Poolish',
  'formula.add.tangzhong': '+ Tangzhong',
  'formula.prep.flourField': 'Part de la farine',
  'formula.prep.hydrationField': 'Hydratation',
  'formula.changes.title': 'Modifications non enregistrées',
  'formula.changes.added': '{name} ajouté',
  'formula.changes.removed': '{name} retiré',
  'formula.changes.changed': '{name}, {field} : {before} → {after}',
  'formula.changes.field.bakerPct': '% boulanger',
  'formula.changes.field.sharePct': 'part',
  'formula.changes.field.flourPct': 'part de la farine',
  'formula.changes.field.hydrationPct': 'hydratation',
  'formula.changes.locked': 'Hydratation verrouillée à {pct}',
  'formula.changes.unlocked': 'Hydratation déverrouillée',
  'formula.changes.anchor': 'Taille de la recette modifiée',
  'formula.time.title': 'Temps',
  'fermentation.title': 'Fermentation',
  'fermentation.provisional': 'provisoire',
  'fermentation.mode.basic': 'De base',
  'fermentation.mode.advanced': 'Avancé',
  'fermentation.room': 'Température ambiante',
  'fermentation.target': 'Température de pâte visée',
  'fermentation.friction': 'Friction du pétrin',
  'fermentation.water': 'Eau à {temperature}',
  'fermentation.note.friction_not_counted': 'friction du pétrin non comptée',
  'fermentation.note.preferment_at_room_temperature': 'préferment pris à l’ambiante',
  'fermentation.note.flour_at_room_temperature': 'farine à l’ambiante',
  'fermentation.note.water_below_0C': 'sous {low} : impossible avec de l’eau seule',
  'fermentation.note.water_above_60C': 'au-dessus de {high} : trop chaud pour la levure',
  'fermentation.stepTemperature': '{step}, température',
  'fermentation.unbounded': 'pas atteinte en {days} jours',
  'fermentation.reachedBefore': 'La cible est atteinte avant {step}.',
  'fermentation.applyDuration': 'Prendre ce temps',
  'fermentation.progress': 'Le plan atteint {pct} de la maturité',
  'fermentation.readiness': 'Cible « prête à cuire »',
  'fermentation.readiness.model': 'Point final du modèle',
  'fermentation.readiness.experimental': 'Expérimentale (×2)',
  'fermentation.readinessHint.model':
    'Le point final du modèle (« à peu près doublé une fois »), non calibré. Par défaut.',
  'fermentation.readinessHint.experimental':
    'Une hypothèse, non mesurée : environ deux fois le temps du modèle, tirée d’horaires planifiés. Sa fourchette est une plage de sensibilité.',
  'fermentation.bandOutsideDomain':
    'Une partie de la fourchette de levure sort du domaine de dose du modèle.',
  'fermentation.reason.readiness_profile_unknown':
    'Cette recette utilise une cible « prête à cuire » inconnue de cette version.',
  'fermentation.reason.invalid_readiness_profile': 'La cible « prête à cuire » n’est pas valide.',
  'fermentation.reason.non_finite_result': 'Le modèle ne donne pas de résultat fini ici.',
  'fermentation.extrapolated': 'Hors des données du modèle : {reasons}',
  'fermentation.extra.preferment': 'préferment',
  'fermentation.extra.sugar_above_5pct': 'plus de 5 % de sucre',
  'fermentation.extra.fat_above_3pct': 'plus de 3 % de gras',
  'fermentation.extra.salt_outside_1_5_to_3pct': 'sel hors de 1,5 à 3 %',
  'fermentation.extra.water_outside_55_to_75pct': 'hydratation hors de 55 à 75 %',
  'fermentation.extra.sugar_unknown': 'sucre inconnu',
  'fermentation.extra.fat_unknown': 'gras inconnu',
  'fermentation.extra.water_unknown': 'hydratation inconnue',
  'fermentation.reason.no_commercial_yeast': 'Aucun ingrédient n’est marqué comme levure.',
  'fermentation.reason.temperature_unknown': 'Entrer la température ambiante pour avoir les temps.',
  'fermentation.reason.dose_outside_domain':
    'La dose de levure sort du domaine du modèle (0,01 à 3 % en équivalent levure fraîche).',
  'fermentation.reason.temperature_outside_domain':
    'Une température sort du domaine du modèle ({low} à {high}).',
  'fermentation.reason.end_point_not_reached':
    'La pâte n’atteindrait pas son point final en trois semaines.',
  'fermentation.reason.no_mix_step': 'Le processus n’a pas d’étape de pétrissage.',
  'fermentation.reason.no_fermentation_step': 'Aucune étape de fermentation après le pétrissage.',
  'fermentation.reason.formula_invalid': 'La formule n’est pas valide.',
  'fermentation.reason.preferment_yeast_unknown':
    'La levure de ce préferment est inconnue : lui donner de la levure.',
  'fermentation.reason.no_preferment_step': 'Ce préferment n’a pas d’étape.',
  'fermentation.reason.duration_unknown':
    'Donner un temps à chaque étape pour avoir la dose de levure.',
  'fermentation.reason.other': 'Pas d’estimation ({reason}).',
  'fermentation.markYeast': 'Quel ingrédient est la levure ?',
  'fermentation.yeastForm': 'Forme de levure',
  'fermentation.form.active-dry': 'sèche active',
  'fermentation.form.instant': 'instantanée',
  'fermentation.form.fresh': 'fraîche',
  'fermentation.markYeastConfirm': 'Marquer comme levure',
  'error.IncompatibleConstraint.waterTarget.locked':
    'Cette eau suit l’hydratation verrouillée : changer l’hydratation ou la déverrouiller.',
  'error.IncompatibleConstraint.waterTarget.unknown':
    'Impossible de verrouiller l’hydratation : la teneur en eau de {ingredients} est inconnue.',
  'error.IncompatibleConstraint.waterTarget.tooLow':
    'Les autres ingrédients apportent déjà plus d’eau que cette hydratation.',
  'error.IncompatibleConstraint.allocation':
    'Une préparation utilise cet ingrédient : le retirer d’abord de la préparation.',
  'recipes.card.durationAtLeast': '≥ {duration}',
  'recipe.retryStart': 'Le démarrage précédent n’a pas été confirmé : il est renvoyé tel quel.',
  'check.intro':
    'Les points à corriger bloquent l’enregistrement et le démarrage ; les autres sont des conseils.',
  'formula.prep.empty':
    'Aucune préparation. Un poolish ou un tangzhong prépare une partie de la farine avant le pétrissage.',
  'process.plan': 'Planification',
  'fermentation.waterTerm': 'Température de l’eau',
  'fermentation.extrapolationTerm': 'Hors des données du modèle',
  'fermentation.timeTerm': 'Temps du modèle et fourchette',
  'fermentation.schedule': 'Horaire',
  'fermentation.schedule.pick': 'Appliquer un horaire…',
  'fermentation.schedule.confirmTitle': 'Appliquer « {name} » ?',
  'fermentation.schedule.confirmMessage':
    'Les étapes de fermentation sont remplacées ({steps}). Les rabats du pointage sont gardés.',
  'fermentation.schedule.apply': 'Appliquer',
  'fermentation.schedule.refused':
    'Cet horaire ne peut pas remplacer ces étapes : l’une ajoute des ingrédients ou a une technique autre que des rabats.',
  'fermentation.schedule.term': 'Horaires de fermentation',
  'schedule.bulk-proof': 'Pointage et apprêt',
  'schedule.cold-bulk': 'Pointage lent au froid',
  'schedule.cold-proof': 'Apprêt bloqué au froid',
  'schedule.same-day-short': 'Même jour, court',
  'fermentation.doughSteps': 'Étapes de la pâte',
  'fermentation.noDoughSteps': 'Aucune étape de fermentation entre le pétrissage et la cuisson.',
  'fermentation.location.room': 'ambiante',
  'fermentation.location.fridge': 'frigo',
  'fermentation.stepDuration': '{step}, durée',
  'fermentation.auto': 'Auto',
  'fermentation.autoLabel': '{step} : durée selon le modèle',
  'fermentation.autoTerm': 'Durée selon le modèle (Auto)',
  'fermentation.autoValue': '{step} : environ {duration} selon le modèle, détails',
  'fermentation.autoDetail':
    'Selon le modèle : {duration}, entre {low} et {high} selon ses paramètres.',
  'fermentation.autoKept':
    'Le modèle ne peut pas l’estimer pour l’instant : le dernier temps est gardé.',
  'fermentation.autoOff': 'Désactiver Auto pour saisir un temps.',
  'fermentation.removeStep': 'Retirer {step}',
  'fermentation.removeConfirm': 'Retirer l’étape « {step} » ?',
  'fermentation.remove': 'Retirer',
  'fermentation.cancel': 'Annuler',
  'fermentation.add': '+ Ajouter une étape de fermentation',
  'fermentation.add.operation': 'Type d’étape',
  'fermentation.add.after': 'Placée après',
  'fermentation.add.confirm': 'Ajouter l’étape',
  'fermentation.result.covers': 'Le plan atteint {pct} de la cible',
  'fermentation.result.time': '{step} : environ {duration} selon le modèle',
  'fermentation.details': 'Détails',
  'fermentation.detail.time': 'Selon le modèle, {step} : {duration}, entre {low} et {high}.',
  'fermentation.detail.timeUnbounded':
    'Selon le modèle, {step} : {duration} ; pas de fourchette fiable.',
  'fermentation.detail.progress':
    'Selon les paramètres du modèle, le plan atteint entre {low} et {high} de la cible.',
  'fermentation.detail.yeast':
    '{name} pour ces temps : entre {low} et {high} selon les paramètres du modèle.',
  'fermentation.yeastFor': 'Levure pour ces temps : {pct}',
  'fermentation.yeastForNamed': '{name} pour ces temps : {pct}',
  'fermentation.yeastCurrent': '(dose actuelle)',
  'fermentation.useYeast': 'Utiliser',
  'fermentation.useYeastLabel': 'Utiliser {pct} de {name}',
  'fermentation.preferments': 'Préferments',
  'fermentation.yeastName': 'Levure',
  'fermentation.prefermentDetail':
    'Selon le modèle, mûr après {duration}, entre {low} et {high} selon ses paramètres.',
  'fermentation.detail.progressRipe':
    'Selon les paramètres du modèle, le plan atteint entre {low} et {high} de la maturité.',
  'fermentation.prefermentRipe': 'Mûr après environ {duration} selon le modèle',
  'name.all-purpose-flour': 'Farine blanche tout usage',
  'name.bise-flour': 'Farine bise',
  'name.whole-grain-wheat': 'Farine de blé intégral',
  'flours.title': 'Farines',
  'flours.new': 'Nouvelle farine',
  'flours.newName': 'Ma farine',
  'flours.personal': 'Farines personnelles',
  'flours.builtIn': 'Farines de base',
  'flours.empty':
    'Aucune farine personnelle pour l’instant. Personnaliser une farine de base ou en créer une.',
  'flours.showArchived': 'Afficher les farines archivées',
  'flours.archived': 'Archivée',
  'flours.version': 'Version {version}',
  'flours.basedOn': 'D’après {name}',
  'flours.known':
    '{count, plural, =0 {Aucune propriété connue} one {# propriété connue} other {# propriétés connues}}',
  'flours.builtInNote':
    'Une farine de base décrit une catégorie, pas un sac : ses valeurs sont typiques et non vérifiées (≈). Elle ne se modifie pas : la personnaliser pour noter ce qu’indiquent le sac ou le meunier.',
  'flours.typical': 'Valeurs typiques',
  'flours.estimateValue': '≈ {value} ± {uncertainty}',
  'flours.estimatedHint': 'Valeur typique de la catégorie : la remplacer par celle du sac.',
  'flours.summary.estimate': '≈ {value}',
  'flours.summary.proteinPct': 'Protéines {value}',
  'flours.summary.ashPct': 'Cendres {value}',
  'flours.summary.fallingNumberS': 'Indice de chute {value}',
  'flours.summary.alveographW': 'W {value}',
  'flours.summary.alveographPL': 'P/L {value}',
  'flours.summary.malted': 'maltée',
  'flours.summary.organic': 'bio',
  'flour.all-purpose-flour.description':
    'Farine de blé blanche pour la plupart des usages : pains, gâteaux, biscuits. Au Canada, elle est moulue à partir de blés forts et convient au pain.',
  'flour.bread-flour.description':
    'Farine de blé blanche plus riche en protéines que la tout usage, pour les pains qui demandent un gluten fort.',
  'flour.bise-flour.description':
    'Farine à haut taux d’extraction, entre la blanche et l’intégrale : une partie du son est conservée. Pour les pains de campagne.',
  'flour.whole-wheat.description':
    'Au Canada, farine qui garde le son mais dont une partie du germe peut être retirée (à vérifier). Pour les pains de blé entier, souvent en mélange avec de la farine blanche.',
  'flour.whole-grain-wheat.description':
    'Farine moulue à partir du grain entier : son, germe et amande. Pour les pains intégraux.',
  'flour.flour.description':
    'Farine générique, conservée pour les recettes qui la nomment sans préciser laquelle.',
  'flour.spelt-flour.description':
    'Farine d’épeautre, une ancienne espèce de blé. Son gluten est plus fragile que celui du blé tendre ; elle peut être blanche ou intégrale.',
  'flour.brown-flour.description':
    'Farine de blé qui garde une partie du son, entre la blanche et le blé entier.',
  'flour.tipo-00-flour.description':
    'Catégorie italienne de la farine blanche la plus finement moulue. Ses protéines dépendent de l’usage : les farines à pizza sont les plus fortes.',
  'flour.cornmeal.description':
    'Maïs séché moulu grossièrement, sans protéines qui forment du gluten. Pour le pain de maïs, la polenta ou pour fariner une pelle.',
  'flours.customize': 'Personnaliser',
  'flours.save': 'Enregistrer',
  'flours.saving': 'Enregistrement…',
  'flours.archive': 'Archiver',
  'flours.unarchive': 'Désarchiver',
  'flours.close': 'Fermer',
  'flours.identity': 'Farine',
  'flours.properties': 'Propriétés',
  'flours.field.name': 'Nom',
  'flours.field.cereal': 'Céréale',
  'flours.field.refinement': 'Extraction',
  'flours.field.classificationSystem': 'Système de classification (CA, FR, IT…)',
  'flours.field.classificationCode': 'Code de classification (T65, 00…)',
  'flours.field.brand': 'Marque',
  'flours.field.product': 'Produit',
  'flours.field.notes': 'Notes',
  'flours.field.organic': 'Biologique',
  'flours.field.value': '{property} : valeur',
  'flours.field.basis': '{property} : base',
  'flours.field.source': '{property} : source',
  'flours.field.detail': '{property} : précision sur la source (facultatif)',
  'flours.cereal.wheat': 'Blé',
  'flours.cereal.spelt': 'Épeautre',
  'flours.cereal.rye': 'Seigle',
  'flours.cereal.corn': 'Maïs',
  'flours.cereal.other': 'Autre',
  'flours.cereal.unknown': 'Inconnue',
  'flours.refinement.white': 'Blanche',
  'flours.refinement.partial': 'Partielle (une partie du son)',
  'flours.refinement.wholeGrain': 'Grain entier',
  'flours.refinement.unknown': 'Inconnue',
  'flours.prop.proteinPct': 'Protéines (%)',
  'flours.prop.ashPct': 'Cendres (%)',
  'flours.prop.fallingNumberS': 'Indice de chute (s)',
  'flours.prop.alveographW': 'W de l’alvéographe (10⁻⁴ J)',
  'flours.prop.alveographPL': 'P/L de l’alvéographe',
  'flours.prop.malted': 'Malt ajouté par le meunier',
  'flours.unknown': 'Inconnu',
  'flours.yes': 'Oui',
  'flours.no': 'Non',
  'flours.basis.asIs': 'Tel que vendu',
  'flours.basis.moisture14': 'À 14 % d’humidité',
  'flours.basis.dryMatter': 'Sur matière sèche',
  'flours.source.package': 'Étiquette de l’emballage',
  'flours.source.producer': 'Fiche du meunier ou du producteur',
  'flours.source.lab': 'Analyse de laboratoire',
  'flours.source.reference': 'Référence publiée',
  'flours.source.personal': 'Jugement personnel',
  'flours.invalidNumber': 'Nombre non valide.',
  'flours.invalid': 'Certaines valeurs ne sont pas valides : vérifier les champs marqués.',
  'flours.conflict':
    'Cette farine a changé entre-temps. Elle a été rechargée : refaire la modification.',
  'flours.nameRequired': 'Une farine personnelle a besoin d’un nom.',
  'error.InvalidFlourEntry': 'Cette fiche de farine n’est pas valide.',
  'advanced.flours': 'Farines',
  'advanced.floursHint': 'Farines de base et personnelles, avec ce qui en est connu',
  // Mode simulation (ADR 0014)
  'sim.banner': 'Mode simulation · heure simulée {time}',
  'sim.advance': 'Avancer le temps',
  'sim.advance.next': 'Au prochain moment prévu',
  'sim.advance.nextAt': '{time} · {step}',
  'sim.advance.none': 'Aucun moment prévu à venir.',
  'sim.advance.quarter': '+15 min',
  'sim.advance.hour': '+1 h',
  'advanced.simulation': 'Simulation',
  'advanced.simulation.toggle': 'Mode simulation',
  'advanced.simulation.hint':
    'Les fournées démarrées pendant ce mode sont simulées : elles ont leur propre historique et leurs statistiques, et le temps peut être avancé. Recettes, brouillons et farines sont communs.',
  'advanced.history': 'Historique des fournées',
  'advanced.stats': 'Statistiques',
  'advanced.statsHint': 'Fournées du mode actuel, réel ou simulation',
  'advanced.eraseReal': 'Effacer l’historique réel',
  'advanced.eraseSimulation': 'Effacer l’historique de simulation',
  'advanced.eraseHint': 'Toutes les fournées de ce type, en cours comprises. Les recettes restent.',
  'advanced.eraseConfirm':
    '{count, plural, =0 {Aucune fournée à effacer.} one {# fournée sera effacée, en cours comprises.} other {# fournées seront effacées, en cours comprises.}} C’est irréversible : copier toutes les données avant, pour en garder une trace.',
  'advanced.erase': 'Effacer',
  'advanced.erased':
    '{count, plural, =0 {Aucune fournée effacée.} one {# fournée effacée.} other {# fournées effacées.}}',
  'advanced.eraseFailed': 'L’historique n’a pas pu être effacé.',
  // Appearance / theme
  'advanced.theme': 'Apparence',
  'advanced.theme.system': 'Système',
  'advanced.theme.light': 'Clair',
  'advanced.theme.dark': 'Sombre',
  'advanced.theme.amoled': 'AMOLED',
  'advanced.theme.hint':
    'Système suit le réglage de l’appareil. Clair, Sombre et AMOLED restent fixes.',
  // Unités de température (values-and-units.md)
  'advanced.units': 'Unités de température',
  'advanced.units.oven': 'Four',
  'advanced.units.cold': 'Froid',
  'advanced.units.ambient': 'Ambiante',
  'advanced.units.hint':
    'Four : cuisson, préchauffage, ébullition. Froid : frigo, congélateur, retard. Ambiante : tout le reste, pâte et eau comprises. Les valeurs sont toujours enregistrées en °C.',
  'recipe.startConfirm': 'Démarrer la préparation maintenant ?',
  'recipe.startConfirmSimulation':
    'Mode simulation : la préparation ira dans l’historique de simulation.',
  'recipe.startNow': 'Démarrer',
  // Statistiques (ADR 0014)
  'stats.title': 'Statistiques',
  'stats.mode.physical': 'Fournées réelles',
  'stats.mode.simulation': 'Fournées simulées',
  'stats.empty': 'Aucune fournée pour l’instant dans ce mode.',
  'stats.counts': 'Fournées',
  'stats.total': 'Total',
  'stats.finished': 'Terminées',
  'stats.abandoned': 'Abandonnées',
  'stats.active': 'En cours',
  'stats.recipes': 'Recettes les plus faites',
  'stats.bakeCount': '{count, plural, one {# fournée} other {# fournées}}',
  'stats.durations': 'Temps prévus et enregistrés',
  'stats.durationsHint':
    'Médiane par opération : le plan au démarrage de la fournée face au temps enregistré.',
  'stats.durationLine': '{planned} prévu · {recorded} enregistré · n = {n}',
  'stats.longer': '+{duration}',
  'stats.shorter': '−{duration}',
  'stats.onPlan': 'comme prévu',
  'stats.readiness': 'Marques « pâte prête »',
  'stats.readinessCount': '{count, plural, one {# marque} other {# marques}}',
  'stats.readinessCompared':
    'Temps médian dans l’étape à la marque : {recorded} ; modèle : {model} ({difference}), sur {n, plural, one {# marque} other {# marques}}.',
  'stats.readinessNoModel':
    'Aucune marque sur une étape avec une estimation du modèle pour l’instant.',
  'stats.ratings': 'Note moyenne',
  'stats.ratingLine': '{average}/5 · {n, plural, one {# note} other {# notes}}',
  'stats.recent': 'Dernières fournées',
  'stats.recentLine': '{date} · {status}',
  'stats.nothing': 'Rien d’enregistré pour l’instant.',
};

export const catalogs = { en, fr } as const;
export type Locale = keyof typeof catalogs;

/** Picks the first supported language of the device, English otherwise. */
export const pickLocale = (languages: readonly string[]): Locale => {
  for (const language of languages) {
    const base = language.toLowerCase().split('-')[0];
    if (base === 'fr' || base === 'en') return base;
  }
  return 'en';
};
